import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { branding } from '../../constants/branding';
import { apiService } from '../../services/apiService';
import axiosInstance from '../../services/axiosConfig';
import StrategyPreview from './StrategyPreview';
import RetailKYT from './RetailKYT';
import TradingAccountDepositModal from '../TradingAccountDepositModal';
import TradingAccountWithdrawModal from '../TradingAccountWithdrawModal';
import { FaPlus, FaArrowDown, FaChartLine, FaSpinner } from 'react-icons/fa';
import { formatAmount } from '../../utils/currency';
import { featureFlags } from '../../config/featureFlags';
import StrategyExchangeInfo from './StrategyExchangeInfo';
import TradeEvaluationPanel from './TradeEvaluationPanel';
import { formatTradeClosedDisplay, mapTradesForEvaluation, parseFixedOrderAmount } from '../../utils/tradeWindowEvaluation';
import { useSagemasterEnabled } from '../../hooks/useSagemasterEnabled';

interface EligibilityCheck {
  isEligible: boolean;
  reasons: string[];
  riskScore: number;
  maxAllocation: number;
}

interface Bot {
  id: number;
  name: string;
  symbol: string;
  timeframe: string;
  strategy: string;
  maxNotionalUsd: number;
  status: string;
}

interface BotAllocation {
  botId: number;
  botName: string;
  symbol: string;
  strategy: string;
  userAllocation: number;
  maxAllocation: number;
  status: 'ACTIVE' | 'PENDING' | 'REJECTED';
}

interface ConsentForm {
  riskAcknowledgment: boolean;
  termsAccepted: boolean;
  dataProcessingConsent: boolean;
  tradingConsent: boolean;
}

interface WalletBalance {
  totalBalance: number;
  allocatedBalance: number;
  availableBalance: number;
  currency: string;
}

const RetailTradingInterface: React.FC = () => {
  const sagemasterEnabled = useSagemasterEnabled('retail');
  const navigate = useNavigate();
  const profile = useAppSelector((state) => state.profile.profile);
  const location = useLocation();
  const [eligibility, setEligibility] = useState<EligibilityCheck | null>(null);
  const [bots, setBots] = useState<Bot[]>([]);
  const [allocations, setAllocations] = useState<BotAllocation[]>([]);
  const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null);
  const [consent, setConsent] = useState<ConsentForm>({
    riskAcknowledgment: false,
    termsAccepted: false,
    dataProcessingConsent: false,
    tradingConsent: false
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'eligibility' | 'consent' | 'allocation' | 'strategies' | 'crypto-wallets' | 'kyt' | 'pnl-history'>('eligibility');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Crypto wallet state
  const [userCryptoWallets, setUserCryptoWallets] = useState<any[]>([]);
  const [cryptoAllocations, setCryptoAllocations] = useState<any[]>([]);
  const [showCryptoAllocationModal, setShowCryptoAllocationModal] = useState(false);
  const [selectedBotForCrypto, setSelectedBotForCrypto] = useState<number | null>(null);
  const [selectedWalletForAllocation, setSelectedWalletForAllocation] = useState<any>(null);
  const [allocationAmount, setAllocationAmount] = useState('');
  const [allocationNotes, setAllocationNotes] = useState('');

  // User's retail subscription plan (for tier-gating strategies)
  const [userSubscription, setUserSubscription] = useState<{
    planName: string;
    price: number;
    maxStrategies: number;
    hasAccess: boolean;
  } | null>(null);
  const [allRetailPlans, setAllRetailPlans] = useState<{ name: string; price: number }[]>([]);

  // Trading strategies state
  const [tradingStrategies, setTradingStrategies] = useState<any[]>([]);
  const [strategyAllocations, setStrategyAllocations] = useState<any[]>([]);
  const [showStrategyAllocationModal, setShowStrategyAllocationModal] = useState(false);
  const [selectedStrategyForAllocation, setSelectedStrategyForAllocation] = useState<number | null>(null);
  const [strategyAllocationAmount, setStrategyAllocationAmount] = useState('');
  const [strategyAllocationNotes, setStrategyAllocationNotes] = useState('');
  const [strategyCapacity, setStrategyCapacity] = useState<{
    maxInvestment: number;
    currentAllocated: number;
    remainingCapacity: number;
    isFull: boolean;
  } | null>(null);
  const [loadingCapacity, setLoadingCapacity] = useState(false);
  
  // Crypto strategy allocation state
  const [showCryptoStrategyAllocationModal, setShowCryptoStrategyAllocationModal] = useState(false);
  const [selectedStrategyForCryptoAllocation, setSelectedStrategyForCryptoAllocation] = useState<number | null>(null);
  const [selectedWalletForStrategyAllocation, setSelectedWalletForStrategyAllocation] = useState<any>(null);
  const [cryptoStrategyAllocationAmount, setCryptoStrategyAllocationAmount] = useState('');
  const [cryptoStrategyAllocationNotes, setCryptoStrategyAllocationNotes] = useState('');
  
  // Strategy analytics state
  const [strategyPnlHistory, setStrategyPnlHistory] = useState<{[key: number]: any[]}>({});
  const [strategyTrades, setStrategyTrades] = useState<{[key: number]: any[]}>({});
  const [strategyPositions, setStrategyPositions] = useState<{[key: number]: any[]}>({});
  const [expandedStrategy, setExpandedStrategy] = useState<number | null>(null);
  
  // Mock data toggle state - per strategy (undefined = auto-detect, true = mock, false = real)
  const [mockDataMode, setMockDataMode] = useState<{[key: number]: boolean | undefined}>({});
  
  // History modal state
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyStrategyId, setHistoryStrategyId] = useState<number | null>(null);
  const [allHistoryTrades, setAllHistoryTrades] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDateRange, setHistoryDateRange] = useState<string>('30D');
  const [historyCoinFilter, setHistoryCoinFilter] = useState<string>('ALL');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(20);

  // Full analytics modal state (KuCoin/DB toggle)
  const [showFullAnalytics, setShowFullAnalytics] = useState(false);
  const [fullAnalyticsStrategyId, setFullAnalyticsStrategyId] = useState<number | null>(null);
  const [fullAnalyticsStrategyName, setFullAnalyticsStrategyName] = useState<string>('');
  const [fullAnalyticsDataSource, setFullAnalyticsDataSource] = useState<'kucoin' | 'db'>('db');
  const [fullAnalyticsLoading, setFullAnalyticsLoading] = useState(false);
  const [fullAnalyticsTrades, setFullAnalyticsTrades] = useState<any[]>([]);
  const [fullAnalyticsPositions, setFullAnalyticsPositions] = useState<any[]>([]);
  const [fullAnalyticsPnlHistory, setFullAnalyticsPnlHistory] = useState<any[]>([]);
  const [fullAnalyticsKucoinSummary, setFullAnalyticsKucoinSummary] = useState<any>(null);
  const [fullAnalyticsError, setFullAnalyticsError] = useState<string | null>(null);

  // Trading account panel state
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [tradingWalletSummary, setTradingWalletSummary] = useState<any>(null);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [tradingAccountLoading, setTradingAccountLoading] = useState(false);

  // TrueLayer top-up history state (TL-KC-US-005 / FE-US-017)
  const [topUpHistory, setTopUpHistory] = useState<any[]>([]);
  const [topUpHistoryLoading, setTopUpHistoryLoading] = useState(false);
  const [topUpHistoryPage, setTopUpHistoryPage] = useState(0);
  const [topUpHistoryTotalPages, setTopUpHistoryTotalPages] = useState(0);
  const [showTopUpHistory, setShowTopUpHistory] = useState(false);

  // TL-KC-EPIC-2: P&L distribution history state
  const [pnlHistory, setPnlHistory] = useState<any[]>([]);
  const [pnlHistoryLoading, setPnlHistoryLoading] = useState(false);
  const [pnlHistoryPage, setPnlHistoryPage] = useState(0);
  const [pnlHistoryTotalPages, setPnlHistoryTotalPages] = useState(0);
  const [pnlHistoryTotalElements, setPnlHistoryTotalElements] = useState(0);

  // ----- Lightweight chart helpers (no external deps) -----
  const Sparkline: React.FC<{ points: number[]; width?: number; height?: number; color?: string }> = ({
    points,
    width = 140,
    height = 36,
    color = '#198754' // green
  }) => {
    if (!points || points.length === 0) {
      return (
        <svg width={width} height={height} aria-label="sparkline" />
      );
    }

    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = max - min || 1;
    const stepX = width / (points.length - 1);
    const toY = (v: number) => height - ((v - min) / range) * height;
    const d = points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * stepX} ${toY(v)}`).join(' ');

    const last = points[points.length - 1];
    const first = points[0];
    const isUp = last >= first;
    const strokeColor = isUp ? color : '#dc3545'; // red if down

    return (
      <svg width={width} height={height} aria-label="sparkline">
        <path d={d} fill="none" stroke={strokeColor} strokeWidth={2} />
      </svg>
    );
  };

  // Helper function to detect if data is mock
  const isPaperTradingStrategy = (strategyId: number): boolean => {
    const strategy = tradingStrategies.find(s => s.id === strategyId);
    if (strategy) {
      const config = parseStrategyConfig(strategy.config || '{}');
      return config.paperTrading === true;
    }
    return false;
  };

  // Helper function to check if there are any real money live bots
  const hasRealMoneyLiveBots = (): boolean => {
    return tradingStrategies.some(strategy => {
      const config = parseStrategyConfig(strategy.config || '{}');
      return config.paperTrading !== true; // Real money if paperTrading is false, undefined, or null
    });
  };
  
  const isMockData = (strategyId: number): boolean => {
    // Paper trading strategies always use real market data (not mock)
    // The trades are simulated but use real prices from Kucoin
    if (isPaperTradingStrategy(strategyId)) {
      return false; // Always show as "Real Data" for paper trading
    }
    
    // If user has explicitly toggled to mock mode, return true
    if (mockDataMode[strategyId] === true) {
      return true;
    }
    
    // If user has explicitly toggled to real mode, return false
    if (mockDataMode[strategyId] === false) {
      return false;
    }
    
    // Auto-detect based on data
    const trades = strategyTrades[strategyId] || [];
    const positions = strategyPositions[strategyId] || [];
    
    // Mock data indicators:
    // 1. No trades have closedAt or exitReason (mock trades don't have these)
    // 2. All trades are BUY/SELL (no CLOSE trades with exit reasons)
    // 3. No positions or positions seem random
    
    if (trades.length === 0 && positions.length === 0) {
      // No data at all - could be mock or just no activity
      return false; // Don't show indicator if there's no data
    }
    
    // Check if any trades have closedAt/exitReason (real data indicators)
    const hasRealTradeData = trades.some((trade: any) => 
      trade.closedAt || (trade.exitReason && trade.side === 'CLOSE')
    );
    
    // If we have trades but none have closedAt/exitReason, it's likely mock data
    if (trades.length > 0 && !hasRealTradeData) {
      return true;
    }
    
    return false;
  };
  
  // Toggle mock data mode for a strategy
  const toggleMockDataMode = async (strategyId: number) => {
    // Disable toggle for paper trading strategies
    if (isPaperTradingStrategy(strategyId)) {
      return; // Do nothing - toggle is disabled for paper trading
    }
    
    const currentMode = mockDataMode[strategyId];
    
    // Cycle through: undefined (auto) → true (mock) → false (real) → undefined (auto)
    let newMode: boolean | undefined;
    if (currentMode === undefined) {
      newMode = true; // Auto → Mock
    } else if (currentMode === true) {
      newMode = false; // Mock → Real
    } else {
      newMode = undefined; // Real → Auto
    }
    
    // Update toggle state
    setMockDataMode(prev => ({ ...prev, [strategyId]: newMode }));
    
    // Always refetch data when toggling
    // Expand the card if not already expanded to show the data
    if (expandedStrategy !== strategyId) {
      setExpandedStrategy(strategyId);
    }
    // Pass newMode to avoid timing issues with state updates
    // When newMode is undefined, calculate what isMockData would return
    let overrideMode: boolean | undefined = newMode;
    if (newMode === undefined) {
      // Auto-detect: check current data
      const trades = strategyTrades[strategyId] || [];
      const positions = strategyPositions[strategyId] || [];
      const hasRealTradeData = trades.some((trade: any) => 
        trade.closedAt || (trade.exitReason && trade.side === 'CLOSE')
      );
      overrideMode = trades.length > 0 && !hasRealTradeData;
    }
    await fetchStrategyAnalytics(strategyId, overrideMode);
  };

  // Fetch strategy analytics data
  const fetchStrategyAnalytics = async (strategyId: number, overrideMode?: boolean | undefined) => {
    try {
      // For paper trading strategies, always use real data (explicitly set forceMock=false)
      // Paper trading uses real market data from Kucoin, so we fetch the simulated trades
      let forceMockParam = '';
      if (isPaperTradingStrategy(strategyId)) {
        // Explicitly set forceMock=false for paper trading to prevent mock data fallback
        forceMockParam = '&forceMock=false';
      } else {
        // For non-paper trading strategies, determine if we should use mock data
        // If overrideMode is provided, use it (for immediate toggle updates)
        // Otherwise, use isMockData() to match what the toggle switch displays
        let shouldUseMock: boolean;
        if (overrideMode !== undefined) {
          // Use the override mode directly
          shouldUseMock = overrideMode === true;
        } else {
          // Use isMockData() to match what the toggle switch displays
          shouldUseMock = isMockData(strategyId);
        }
        forceMockParam = shouldUseMock ? '&forceMock=true' : '&forceMock=false';
      }
      
      // Fetch P&L history
      const pnlResponse = await apiService.get(`/api/retail/trading/strategies/${strategyId}/pnl-history?days=30${forceMockParam}`);
      if (pnlResponse.success) {
        setStrategyPnlHistory(prev => ({ ...prev, [strategyId]: pnlResponse.data }));
      }

      // Fetch recent trades
      const tradesResponse = await apiService.get(`/api/retail/trading/strategies/${strategyId}/trades?limit=10${forceMockParam}`);
      if (tradesResponse.success) {
        // Handle both direct array response and nested response.data structure
        const tradesData = Array.isArray(tradesResponse.data) 
          ? tradesResponse.data 
          : (tradesResponse.data?.data || tradesResponse.data || []);
        console.log('Recent trades loaded for strategy', strategyId, ':', tradesData.length, 'trades');
        console.log('Sample trade data:', tradesData[0]);
        setStrategyTrades(prev => ({ ...prev, [strategyId]: tradesData }));
      }

      // Fetch open positions - always include forceMock parameter
      const positionsParam = forceMockParam ? forceMockParam.substring(1) : 'forceMock=false';
      const positionsResponse = await apiService.get(`/api/retail/trading/strategies/${strategyId}/positions?${positionsParam}`);
      if (positionsResponse.success) {
        setStrategyPositions(prev => ({ ...prev, [strategyId]: positionsResponse.data }));
      }
    } catch (error) {
      console.error('Failed to fetch strategy analytics:', error);
    }
  };

  // Fetch all history trades for a strategy
  const fetchAllHistoryTrades = async (strategyId: number) => {
    try {
      setHistoryLoading(true);
      
      // For paper trading strategies, always use real data (explicitly set forceMock=false)
      let forceMockParam = '';
      if (isPaperTradingStrategy(strategyId)) {
        // Explicitly set forceMock=false for paper trading to prevent mock data fallback
        forceMockParam = '&forceMock=false';
      } else {
        // Get current mock data mode for this strategy
        // Use isMockData() to match what the toggle switch displays
        const shouldUseMock = isMockData(strategyId);
        forceMockParam = shouldUseMock ? '&forceMock=true' : '&forceMock=false';
      }
      
      // Fetch a large number of trades (1000) to get comprehensive history
      const response = await apiService.get(`/api/retail/trading/strategies/${strategyId}/trades?limit=1000${forceMockParam}`);
      console.log('History API response:', response);
      if (response.success) {
        // Handle both direct array response and nested response.data structure
        const tradesData = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || response.data || []);
        console.log('History trades loaded:', tradesData.length, 'trades');
        console.log('Sample history trade:', tradesData[0]);
        setAllHistoryTrades(tradesData);
      } else {
        console.error('Failed to fetch history trades:', response.error);
        setAllHistoryTrades([]);
      }
    } catch (error) {
      console.error('Failed to fetch history trades:', error);
      setAllHistoryTrades([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Open history modal
  const openHistoryModal = (strategyId: number) => {
    setHistoryStrategyId(strategyId);
    setShowHistoryModal(true);
    setHistoryPage(1);
    setHistoryDateRange('30D');
    setHistoryCoinFilter('ALL');
    fetchAllHistoryTrades(strategyId);
  };

  const fetchRetailFullAnalytics = async (strategyId: number, source: 'kucoin' | 'db') => {
    setFullAnalyticsLoading(true);
    setFullAnalyticsError(null);
    setFullAnalyticsKucoinSummary(null);
    try {
      if (source === 'kucoin') {
        // Fetch KuCoin closed-trade analytics AND live open positions in parallel
        const [res, positionsRes] = await Promise.all([
          apiService.getRetailStrategyKucoinAnalytics(strategyId, 200),
          apiService.getRetailStrategyPositions(strategyId),
        ]);
        if (res.success && res.data) {
          const d = res.data as any;
          setFullAnalyticsTrades(d.closedTrades || []);
          setFullAnalyticsPnlHistory(d.pnlHistory || []);
          // Always show live open positions alongside KuCoin closed data
          setFullAnalyticsPositions(positionsRes.success && Array.isArray(positionsRes.data) ? positionsRes.data : []);
          setFullAnalyticsKucoinSummary({
            // Backend returns closedPositionCount and netPnlAfterFees
            closedPositions: d.summary?.closedPositionCount ?? d.summary?.closedPositions ?? 0,
            totalPnl: d.summary?.totalPnl ?? 0,
            totalTradeFee: d.summary?.totalTradeFee ?? 0,
            totalFundingFee: d.summary?.totalFundingFee ?? 0,
            netPnl: d.summary?.netPnlAfterFees ?? d.summary?.netPnl ?? 0,
            winRate: d.summary?.winRate ?? 0,
          });
        } else {
          setFullAnalyticsError('KuCoin data unavailable — switch to DB mode');
          setFullAnalyticsTrades([]);
          setFullAnalyticsPnlHistory([]);
          // Still show open positions even if KuCoin closed-trade fetch failed
          setFullAnalyticsPositions(positionsRes.success && Array.isArray(positionsRes.data) ? positionsRes.data : []);
        }
      } else {
        const [tradesRes, positionsRes, pnlRes] = await Promise.all([
          apiService.getRetailStrategyTrades(strategyId, 50),
          apiService.getRetailStrategyPositions(strategyId),
          apiService.getRetailStrategyPnlHistory(strategyId, 30),
        ]);
        setFullAnalyticsTrades(tradesRes.success && Array.isArray(tradesRes.data) ? tradesRes.data : []);
        setFullAnalyticsPositions(positionsRes.success && Array.isArray(positionsRes.data) ? positionsRes.data : []);
        setFullAnalyticsPnlHistory(pnlRes.success && Array.isArray(pnlRes.data) ? pnlRes.data : []);
      }
    } catch (err) {
      setFullAnalyticsError('Failed to load analytics data');
    } finally {
      setFullAnalyticsLoading(false);
    }
  };

  const openFullAnalytics = (strategyId: number, strategyName: string) => {
    setFullAnalyticsStrategyId(strategyId);
    setFullAnalyticsStrategyName(strategyName);
    setFullAnalyticsDataSource('db');
    setFullAnalyticsTrades([]);
    setFullAnalyticsPositions([]);
    setFullAnalyticsPnlHistory([]);
    setFullAnalyticsKucoinSummary(null);
    setFullAnalyticsError(null);
    setShowFullAnalytics(true);
    fetchRetailFullAnalytics(strategyId, 'db');
  };

  const fullAnalyticsOrderSize = useMemo(() => {
    if (!fullAnalyticsStrategyId) return undefined;
    const strategy = tradingStrategies.find((s) => s.id === fullAnalyticsStrategyId);
    return strategy ? parseFixedOrderAmount(strategy.config) : undefined;
  }, [fullAnalyticsStrategyId, tradingStrategies]);

  // Filter history trades based on date range and coin
  const filterHistoryTrades = () => {
    let filtered = [...allHistoryTrades];

    // Filter by date range
    if (historyDateRange !== 'ALL') {
      const now = new Date();
      let cutoffDate = new Date();
      
      switch (historyDateRange) {
        case '7D':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case '30D':
          cutoffDate.setDate(now.getDate() - 30);
          break;
        case '90D':
          cutoffDate.setDate(now.getDate() - 90);
          break;
      }
      
      filtered = filtered.filter((trade: any) => {
        const tradeDate = new Date(trade.timestamp || trade.closedAt || trade.createdAt || trade.entryTime || trade.exitTime);
        return tradeDate >= cutoffDate;
      });
    }

    // Filter by coin
    if (historyCoinFilter !== 'ALL') {
      filtered = filtered.filter((trade: any) => 
        trade.symbol && trade.symbol.toUpperCase() === historyCoinFilter.toUpperCase()
      );
    }

    // Sort by date (newest first)
    filtered.sort((a: any, b: any) => {
      const dateA = new Date(a.timestamp || a.closedAt || a.createdAt || a.entryTime || a.exitTime).getTime();
      const dateB = new Date(b.timestamp || b.closedAt || b.createdAt || b.entryTime || b.exitTime).getTime();
      return dateB - dateA;
    });

    return filtered;
  };

  // Deterministic pseudo-random generator for placeholder history (stable per key)
  const generateSparklineData = (key: string, length = 30): number[] => {
    let seed = 0;
    for (let i = 0; i < key.length; i++) seed = (seed * 31 + key.charCodeAt(i)) >>> 0;
    const data: number[] = [];
    let value = 0;
    for (let i = 0; i < length; i++) {
      // bounded random walk
      seed = (1664525 * seed + 1013904223) >>> 0;
      const rnd = (seed % 1000) / 1000; // 0..1
      const step = (rnd - 0.48) * 0.6; // small drift
      value += step;
      data.push(Number(value.toFixed(3)));
    }
    return data;
  };

  // Enhanced sparkline with real data
  const getRealSparklineData = (strategyId: number): number[] => {
    const history = strategyPnlHistory[strategyId];
    if (history && history.length > 0) {
      return history.map((entry: any) => parseFloat(entry.cumulativePnl) || 0);
    }
    // Fallback to generated data if no real data
    return generateSparklineData(`strategy-${strategyId}`);
  };

  // Helper function to parse strategy config
  const parseStrategyConfig = (configString: string) => {
    try {
      return JSON.parse(configString);
    } catch (error) {
      console.error('Failed to parse strategy config:', error);
      return {};
    }
  };

  // Fetch trading strategies
  const fetchTradingStrategies = async () => {
    try {
      const response = await apiService.get('/api/retail/trading/strategies');
      if (response.success) {
        setTradingStrategies(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch trading strategies:', error);
    }
  };

  // Fetch user's subscription status + all plans (for tier-gating)
  const fetchSubscriptionContext = async () => {
    try {
      const [statusRes, plansRes] = await Promise.all([
        apiService.get('/api/retail/subscription/status'),
        apiService.get('/api/retail/subscription/plans'),
      ]);
      if (statusRes.success && statusRes.data?.planName && statusRes.data?.planName !== 'N/A') {
        setUserSubscription({
          planName: statusRes.data.planName,
          price: statusRes.data.price || 0,
          maxStrategies: statusRes.data.maxStrategies || 999,
          hasAccess: statusRes.data.hasAccess ?? true,
        });
      }
      if (plansRes.success && Array.isArray(plansRes.data)) {
        setAllRetailPlans(plansRes.data.map((p: any) => ({ name: p.name, price: p.priceMonthly || 0 })));
      }
    } catch (err) {
      console.error('Failed to fetch subscription context:', err);
    }
  };

  /**
   * Returns true if the current user's subscription plan meets the strategy's
   * requiredPlan tier (or if there is no requirement).
   */
  const isStrategyAccessible = (strategy: any): boolean => {
    if (!strategy.requiredPlan) return true; // No restriction
    if (!userSubscription) return false;      // User has no subscription

    const requiredPlan = allRetailPlans.find(
      p => p.name.toLowerCase() === strategy.requiredPlan.toLowerCase()
    );
    if (!requiredPlan) return true; // Unknown plan name — allow by default

    // User's plan must have a price >= required plan price
    return userSubscription.price >= requiredPlan.price;
  };

  // Fetch strategy investment capacity
  const fetchStrategyCapacity = async (strategyId: number) => {
    setLoadingCapacity(true);
    try {
      const response = await apiService.get(`/api/retail/trading/strategies/${strategyId}/capacity`);
      if (response.success && response.data) {
        setStrategyCapacity({
          maxInvestment: response.data.maxInvestment || 0,
          currentAllocated: response.data.currentAllocated || 0,
          remainingCapacity: response.data.remainingCapacity || 0,
          isFull: response.data.isFull || false
        });
      }
    } catch (error) {
      console.error('Failed to fetch strategy capacity:', error);
      setStrategyCapacity(null);
    } finally {
      setLoadingCapacity(false);
    }
  };

  // Fetch user's strategy allocations
  const fetchStrategyAllocations = async () => {
    try {
      const userId = profile?.id;
      if (!userId) return;
      
      const response = await apiService.get(`/api/retail/trading/strategy-allocations/${userId}`);
      if (response.success) {
        setStrategyAllocations(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch strategy allocations:', error);
    }
  };

  // Allocate funds to a trading strategy
  const allocateToStrategy = async () => {
    if (!selectedStrategyForAllocation || !strategyAllocationAmount) return;
    
    try {
      setLoading(true);
      const userId = profile?.id;
      if (!userId) return;
      
      // Check if an allocation already exists for this strategy
      const existingAllocation = (strategyAllocations || []).find((a: any) => {
        const allocStrategyId = a.strategy?.id || a.strategyId || a.strategy_id;
        return String(allocStrategyId) === String(selectedStrategyForAllocation);
      });
      
      let response;
      const payload = {
        userId,
        strategyId: selectedStrategyForAllocation,
        amount: parseFloat(strategyAllocationAmount),
        notes: strategyAllocationNotes
      };
      
      if (existingAllocation) {
        // User already has an allocation for this strategy - update the amount instead of creating a new one
        // Backend expects the amount to be the NEW TOTAL allocation for this user+strategy
        response = await apiService.put('/api/retail/trading/strategy-allocations', payload);
      } else {
        // No existing allocation - create a new one
        response = await apiService.post('/api/retail/trading/strategy-allocations', payload);
      }
      
      if (response.success) {
        await fetchStrategyAllocations();
        await fetchWalletBalance();
        setShowStrategyAllocationModal(false);
        setStrategyAllocationAmount('');
        setStrategyAllocationNotes('');
        setSelectedStrategyForAllocation(null);
      }
    } catch (error) {
      console.error('Failed to allocate to strategy:', error);
    } finally {
      setLoading(false);
    }
  };

  // Allocate crypto to a trading strategy
  const allocateCryptoToStrategy = async () => {
    if (!selectedStrategyForCryptoAllocation || !selectedWalletForStrategyAllocation || !cryptoStrategyAllocationAmount) return;
    
    try {
      setLoading(true);
      const userId = profile?.id;
      if (!userId) return;
      
      // Calculate USD value (you might want to get real-time crypto prices)
      const cryptoAmount = parseFloat(cryptoStrategyAllocationAmount);
      const cryptoCurrency = selectedWalletForStrategyAllocation.blockchain;
      const usdValue = cryptoAmount * 50000; // Mock USD value - replace with real crypto price API
      
      const response = await apiService.post('/api/retail/trading/strategy-crypto-allocations', {
        userId,
        strategyId: selectedStrategyForCryptoAllocation,
        walletAddress: selectedWalletForStrategyAllocation.address,
        cryptoAmount: cryptoAmount,
        cryptoCurrency: cryptoCurrency,
        usdValue: usdValue,
        notes: cryptoStrategyAllocationNotes
      });
      
      if (response.success) {
        alert('Crypto strategy allocation created successfully!');
        setShowCryptoStrategyAllocationModal(false);
        setSelectedStrategyForCryptoAllocation(null);
        setSelectedWalletForStrategyAllocation(null);
        setCryptoStrategyAllocationAmount('');
        setCryptoStrategyAllocationNotes('');
        await fetchStrategyAllocations();
        await fetchWalletBalance();
      }
    } catch (error) {
      console.error('Failed to allocate crypto to strategy:', error);
      alert('Failed to allocate crypto to strategy: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch wallet balance from the backend
  const fetchWalletBalance = async () => {
    try {
      // Get user ID from Redux state
      const userId = profile?.id;
      if (!userId) {
        console.error('No user ID available for wallet balance fetch');
        return;
      }
      const retailPerms: string[] = (profile as { permissions?: string[] } | null)?.permissions || [];
      if (!retailPerms.includes('VIEW_RETAIL_TRADING_BOTS')) {
        setWalletBalance(null);
        return;
      }
      console.log('Fetching wallet balance for user:', userId);
      const response = await apiService.get(`/api/retail/trading/wallet-balance/${userId}`);
      console.log('Wallet balance response:', response);
      
      if (response.success && response.data) {
        const balance = response.data;
        // Try to get authoritative allocated total (falls back to balance.allocatedBalance)
        let allocatedTotal = balance.allocatedBalance || 0;
        try {
          const totalAllocResp = await apiService.get(`/api/retail/trading/strategy-allocations/${userId}/total`);
          if (totalAllocResp.success && typeof totalAllocResp.data === 'number') {
            allocatedTotal = totalAllocResp.data;
          }
        } catch (e) {
          console.warn('Failed to fetch total allocated amount, falling back to balance.allocatedBalance');
        }

        const totalBalance = balance.totalBalance || 0;
        const available = Math.max(totalBalance - allocatedTotal, 0);
        console.log('Setting wallet balance with reconciled values:', { totalBalance, allocatedTotal, available });
        setWalletBalance({
          totalBalance,
          allocatedBalance: allocatedTotal,
          availableBalance: available,
          currency: balance.currency || 'USD'
        });
      } else {
        console.warn('No wallet balance data received:', response.error || response.message);
        setWalletBalance(null);
      }
    } catch (error) {
      console.error('Failed to fetch wallet balance:', error);
      setWalletBalance(null);
    }
  };

  // Fetch available trading bots from the backend
  const fetchBots = async (opts?: { skipGlobalLoading?: boolean }) => {
    const useGlobalLoading = !opts?.skipGlobalLoading;
    if (useGlobalLoading) setLoading(true);
    try {
      console.log('Fetching bots from /api/retail/trading/bots/available');
      const response = await apiService.get('/api/retail/trading/bots/available');
      console.log('Bots response:', response);
      
      // Handle different response formats
      let botArray = null;
      if (response.success && response.data && Array.isArray(response.data)) {
        botArray = response.data;
      } else if (Array.isArray(response.data)) {
        botArray = response.data;
      } else if (Array.isArray(response)) {
        botArray = response;
      }
      
      if (botArray && botArray.length > 0) {
        // Convert RetailAllocationDto to Bot format
        const botData = botArray.map((bot: any) => ({
          id: bot.botId,
          name: bot.botName,
          symbol: bot.symbol,
          timeframe: '1h', // Default timeframe
          strategy: bot.strategy,
          maxNotionalUsd: bot.maxAllocation,
          status: bot.status || 'ACTIVE'
        }));
        console.log('Converted bot data:', botData);
        setBots(botData);
      } else {
        console.log('No bots data received or invalid format:', response);
        setBots([]);
      }
    } catch (error) {
      console.error('Failed to fetch bots:', error);
      setBots([]);
    } finally {
      if (useGlobalLoading) setLoading(false);
    }
  };

  const fetchUserCryptoWallets = async () => {
    try {
      // Use the same endpoint and method as the crypto portfolio page
      const response = await axiosInstance.get('/crypto/wallet');
      
      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats: data.success or data.status === 'success'
        if (data.success || data.status === 'success') {
          setUserCryptoWallets(data.data || []);
          console.log('Fetched crypto wallets:', data.data);
        } else {
          console.error('Failed to fetch crypto wallets:', data.message);
          setUserCryptoWallets([]);
        }
      }
    } catch (error) {
      console.error('Error fetching crypto wallets:', error);
      setUserCryptoWallets([]);
    }
  };

  const fetchCryptoTradingSummary = async () => {
    try {
      const userId = profile?.id;
      if (!userId) {
        console.error('No user ID available for crypto trading summary fetch');
        return;
      }
      const response = await apiService.get(`/api/retail/crypto-trading/summary/${userId}`);
      if (response.success) {
        setCryptoAllocations(response.data?.allocations || []);
      }
    } catch (error) {
      console.error('Failed to fetch crypto trading summary:', error);
    }
  };

  const handleCryptoWalletAllocation = async () => {
    if (!selectedWalletForAllocation || !allocationAmount || !selectedBotForCrypto) {
      alert('Please select a wallet, bot, and enter an amount');
      return;
    }

    try {
      setLoading(true);
      const allocationData = {
        username: 'user1', // Mock username - in real implementation, get from auth context
        botId: selectedBotForCrypto,
        cryptoWalletId: selectedWalletForAllocation.id,
        cryptoAmount: parseFloat(allocationAmount),
        notes: allocationNotes
      };

      const response = await apiService.post('/api/retail/crypto-trading/allocate', allocationData);
      
      if (response.success) {
        alert('Crypto wallet allocated successfully!');
        setShowCryptoAllocationModal(false);
        setSelectedWalletForAllocation(null);
        setSelectedBotForCrypto(null);
        setAllocationAmount('');
        setAllocationNotes('');
        // Refresh data
        fetchCryptoTradingSummary();
        fetchUserCryptoWallets();
      } else {
        alert('Failed to allocate crypto wallet: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error allocating crypto wallet:', error);
      alert('Error allocating crypto wallet: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // Check eligibility by calling the backend
  const checkEligibility = async () => {
    setLoading(true);
    try {
      const userId = profile?.id;
      if (!userId) {
        console.error('No user ID available for eligibility check');
        setLoading(false);
        return;
      }
      console.log('Checking eligibility for user:', userId);
      const response = await apiService.get(`/api/retail/trading/eligibility/${userId}`);
      console.log('Eligibility response:', response);
      
      if (response && response.success && response.data) {
        const eligibilityData = response.data;
        // Accept either isEligible or eligible from backend
        const eligibleFlag =
          (typeof eligibilityData.isEligible === 'boolean' && eligibilityData.isEligible) ||
          (typeof eligibilityData.eligible === 'boolean' && eligibilityData.eligible) ||
          false;
        setEligibility({
          isEligible: eligibleFlag,
          reasons: Array.isArray(eligibilityData.reasons) ? eligibilityData.reasons : [],
          riskScore: Number(eligibilityData.riskScore) || 0,
          maxAllocation: Number(eligibilityData.maxAllocation) || 0
        });
        console.log('Setting eligibility data:', eligibilityData);
      } else {
        console.log('No eligibility data received:', response);
        // Set default values if no data
        setEligibility({
          isEligible: false,
          reasons: ['Unable to determine eligibility'],
          riskScore: 0,
          maxAllocation: 0
        });
      }
    } catch (error) {
      console.error('Failed to check eligibility:', error);
      // Set error state
      setEligibility({
        isEligible: false,
        reasons: ['Error checking eligibility'],
        riskScore: 0,
        maxAllocation: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's existing consent from the backend
  const fetchUserConsent = async () => {
    try {
      const userId = profile?.id;
      if (!userId) {
        console.error('No user ID available for consent fetch');
        return;
      }
      console.log('Fetching user consent for user:', userId);
      const response = await apiService.get(`/api/retail/trading/consent/${userId}`);
      console.log('Consent response:', response);
      
      if (response && response.data && response.data.riskAcknowledgment !== undefined) {
        const consentData = {
          riskAcknowledgment: response.data.riskAcknowledgment || false,
          termsAccepted: response.data.termsAccepted || false,
          dataProcessingConsent: response.data.dataProcessingConsent || false,
          tradingConsent: response.data.tradingConsent || false
        };
        console.log('Setting consent data:', consentData);
        setConsent(consentData);
      } else {
        console.log('No consent data found, keeping default values');
        console.log('Response structure:', {
          response: response,
          hasData: !!response?.data,
          dataKeys: response?.data ? Object.keys(response.data) : 'no data',
          riskAck: response?.data?.riskAcknowledgment
        });
      }
    } catch (error) {
      console.error('Failed to fetch user consent:', error);
      // Keep default false values if fetch fails
    }
  };

  // Fetch user's existing allocations from the backend
  const fetchAllocations = async (opts?: { skipGlobalLoading?: boolean }) => {
    const useGlobalLoading = !opts?.skipGlobalLoading;
    if (useGlobalLoading) setLoading(true);
    try {
      const userId = profile?.id;
      if (!userId) {
        console.error('No user ID available for allocations fetch');
        if (useGlobalLoading) setLoading(false);
        return;
      }
      console.log('Fetching allocations for user:', userId);
      const response = await apiService.get(`/api/retail/trading/allocations/${userId}`);
      console.log('Allocations response:', response);
      
      if (response && response.success && response.data) {
        // Convert backend allocation data to frontend format
        const savedAllocations: BotAllocation[] = response.data.map((allocation: any) => ({
          botId: allocation.botId,
          botName: allocation.botName,
          symbol: allocation.symbol,
          strategy: allocation.strategy,
          userAllocation: allocation.userAllocation || 0,
          maxAllocation: allocation.maxAllocation || 2000,
          status: allocation.status || 'PENDING'
        }));
        
        console.log('Setting saved allocations:', savedAllocations);
        if (savedAllocations.length > 0) {
          setAllocations(savedAllocations);
        } else {
          // Initialize from available bots with max based on available balance
          const botAllocations: BotAllocation[] = bots.map(bot => ({
            botId: bot.id,
            botName: bot.name,
            symbol: bot.symbol,
            strategy: bot.strategy,
            userAllocation: 0,
            maxAllocation: Math.min(bot.maxNotionalUsd ?? 0, walletBalance?.availableBalance ?? 0),
            status: bot.status === 'RUNNING' ? 'ACTIVE' : 'PENDING'
          }));
          setAllocations(botAllocations);
        }
      } else {
        console.log('No saved allocations found, using default format');
        // If no saved allocations, convert bots to allocation format with 0 allocations
        const botAllocations: BotAllocation[] = bots.map(bot => ({
          botId: bot.id,
          botName: bot.name,
          symbol: bot.symbol,
          strategy: bot.strategy,
          userAllocation: 0, // User hasn't allocated yet
          maxAllocation: Math.min(bot.maxNotionalUsd ?? 0, walletBalance?.availableBalance ?? 0), // within available balance
          status: bot.status === 'RUNNING' ? 'ACTIVE' : 'PENDING'
        }));
        
        setAllocations(botAllocations);
      }
    } catch (error) {
      console.error('Failed to fetch allocations:', error);
      // Fallback to default format if fetch fails
      const botAllocations: BotAllocation[] = bots.map(bot => ({
        botId: bot.id,
        botName: bot.name,
        symbol: bot.symbol,
        strategy: bot.strategy,
        userAllocation: 0,
        maxAllocation: Math.min(bot.maxNotionalUsd ?? 0, walletBalance?.availableBalance ?? 0),
        status: bot.status === 'RUNNING' ? 'ACTIVE' : 'PENDING'
      }));
      
      setAllocations(botAllocations);
    } finally {
      if (useGlobalLoading) setLoading(false);
    }
  };

  const handleConsentChange = (field: keyof ConsentForm) => {
    setConsent(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleAllocationChange = (botId: number, newAllocation: number) => {
    // Validate allocation doesn't exceed available balance
    const availableBalance = walletBalance?.availableBalance || 0;
    const otherAllocations = allocations.filter(a => a.botId !== botId).reduce((sum, a) => sum + a.userAllocation, 0);
    
    if (otherAllocations + newAllocation > availableBalance) {
      alert(`Allocation exceeds available balance. Available: $${availableBalance.toLocaleString()}`);
      return;
    }
    
    setAllocations(prev => {
      const existingAllocation = prev.find(a => a.botId === botId);
      if (existingAllocation) {
        // Update existing allocation
        return prev.map(allocation => 
          allocation.botId === botId 
            ? { ...allocation, userAllocation: newAllocation }
            : allocation
        );
      } else {
        // Create new allocation for this bot
        const bot = bots.find(b => b.id === botId);
        if (bot) {
          return [...prev, {
            botId: bot.id,
            botName: bot.name,
            symbol: bot.symbol,
            strategy: bot.strategy,
            userAllocation: newAllocation,
            maxAllocation: bot.maxNotionalUsd || 1000,
            status: newAllocation > 0 ? 'ACTIVE' : 'PENDING'
          }];
        }
        return prev;
      }
    });
  };

  const submitConsent = async () => {
    const allConsentsGiven = Object.values(consent).every(Boolean);
    if (!allConsentsGiven) {
      alert('Please accept all required consents');
      return;
    }
    
    setLoading(true);
    try {
      const userId = profile?.id;
      if (!userId) {
        console.error('No user ID available for consent submission');
        setLoading(false);
        return;
      }
      console.log('Submitting consent for user:', userId, consent);
      const response = await apiService.post(`/api/retail/trading/consent/${userId}`, consent);
      console.log('Consent submission response:', response);
      
      if (response && response.success) {
        alert('Consent submitted successfully');
        setActiveTab('allocation');
      } else {
        alert('Failed to submit consent. Please try again.');
      }
    } catch (error) {
      console.error('Failed to submit consent:', error);
      alert('Failed to submit consent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const submitAllocations = async () => {
    setLoading(true);
    try {
      const userId = profile?.id;
      if (!userId) {
        console.error('No user ID available for allocations submission');
        setLoading(false);
        return;
      }
      console.log('Submitting allocations for user:', userId, allocations);
      
      // Filter out allocations with 0 amount
      const activeAllocations = allocations.filter(allocation => allocation.userAllocation > 0);
      
      if (activeAllocations.length === 0) {
        alert('Please allocate funds to at least one bot');
        setLoading(false);
        return;
      }
      
      const response = await apiService.post(`/api/retail/trading/allocations/${userId}`, activeAllocations);
      console.log('Allocation submission response:', response);
      
      if (response && response.success) {
        alert('Allocations submitted successfully');
        // Refresh wallet balance and allocations to show updated data
        await fetchWalletBalance();
        await fetchAllocations({ skipGlobalLoading: true });
      } else {
        alert('Failed to submit allocations. Please try again.');
      }
    } catch (error) {
      console.error('Failed to submit allocations:', error);
      alert('Failed to submit allocations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch trading wallet summary for panel
  const fetchTradingWalletSummary = async () => {
    if (!profile?.id) return;
    setTradingAccountLoading(true);
    try {
      const response = await apiService.get(`/api/retail/trading/wallets/${profile.id}`);
      if (response.success && response.data) {
        setTradingWalletSummary(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch trading wallet summary:', err);
    } finally {
      setTradingAccountLoading(false);
    }
  };

  // Fetch TrueLayer fiat top-up history (TL-KC-US-005)
  const fetchTopUpHistory = async (page = 0) => {
    if (!profile?.id) return;
    setTopUpHistoryLoading(true);
    try {
      const res = await apiService.get(
        `/api/retail/trading/wallets/${profile.id}/topup-history?page=${page}&limit=10`
      );
      if (res.success) {
        const payload = res.data ?? {};
        setTopUpHistory(payload.data || []);
        setTopUpHistoryPage(payload.page ?? 0);
        setTopUpHistoryTotalPages(payload.totalPages ?? 0);
      }
    } catch (err) {
      console.error('Failed to fetch top-up history:', err);
    } finally {
      setTopUpHistoryLoading(false);
    }
  };

  // TL-KC-EPIC-2: Fetch P&L distribution history
  const fetchPnlHistory = async (page = 0) => {
    if (!profile?.id) return;
    setPnlHistoryLoading(true);
    try {
      const res = await apiService.get(
        `/api/retail/trading/wallets/${profile.id}/pnl-history?page=${page}&limit=20`
      );
      if (res.success) {
        const payload = res.data ?? {};
        setPnlHistory(payload.data || []);
        setPnlHistoryPage(payload.page ?? 0);
        setPnlHistoryTotalPages(payload.totalPages ?? 0);
        setPnlHistoryTotalElements(payload.totalElements ?? 0);
      }
    } catch (err) {
      console.error('Failed to fetch P&L history:', err);
    } finally {
      setPnlHistoryLoading(false);
    }
  };

  // Fetch user accounts for deposit/withdraw
  const fetchAccounts = async () => {
    try {
      const response = await axiosInstance.get('/accounts');
      console.log('Accounts API response:', response);
      
      // Handle different response structures
      let accountsData = null;
      
      // Check if response.data is directly an array (Spring Boot returns List directly)
      if (Array.isArray(response.data)) {
        accountsData = response.data;
      }
      // Check if response.data.data exists (wrapped response)
      else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        accountsData = response.data.data;
      }
      // Check if response.data is an object with accounts property
      else if (response.data && response.data.accounts && Array.isArray(response.data.accounts)) {
        accountsData = response.data.accounts;
      }
      
      if (accountsData && Array.isArray(accountsData)) {
        // Filter to only include CHECKING and SAVINGS accounts (exclude other types)
        const filteredAccounts = accountsData.filter(acc => 
          acc.type && (acc.type.toUpperCase() === 'CHECKING' || acc.type.toUpperCase() === 'SAVINGS')
        );
        
        console.log('Filtered accounts for trading wallet funding:', filteredAccounts);
        setAccounts(filteredAccounts);
      } else {
        console.warn('Accounts response is not in expected format:', response.data);
        setAccounts([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch accounts:', err);
      console.error('Error response:', err.response?.data);
      setAccounts([]);
    }
  };

  useEffect(() => {
    checkEligibility();
    fetchWalletBalance();
    fetchUserConsent();
    fetchUserCryptoWallets();
    fetchCryptoTradingSummary();
    fetchTradingStrategies();
    fetchSubscriptionContext();
    fetchTradingWalletSummary();
    fetchAccounts();
  }, []);

  // Keep bot-allocation rows in sync with the catalog and wallet (also runs when catalog is empty
  // so saved BotAllocation rows still load if the bank hides all bots from the available list).
  useEffect(() => {
    if (!profile?.id) return;
    void fetchAllocations({ skipGlobalLoading: true });
  }, [bots, profile?.id]);

  // Initialize tab from query parameter (?tab=allocation)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const validTabs = ['eligibility','consent','allocation','strategies','crypto-wallets','kyt','pnl-history'];
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab as any);
    }
  }, [location.search]);

  // TL-KC-EPIC-2: Load P&L history when tab is selected
  useEffect(() => {
    if (activeTab === 'pnl-history') {
      fetchPnlHistory(0);
    }
  }, [activeTab]);

  // Refresh crypto wallets when crypto wallets tab is selected
  useEffect(() => {
    if (activeTab === 'crypto-wallets') {
      void fetchUserCryptoWallets();
      void fetchCryptoTradingSummary();
      void fetchBots({ skipGlobalLoading: true });
    }
  }, [activeTab]);

  // Refresh trading strategies when strategies tab is selected
  useEffect(() => {
    if (activeTab === 'strategies') {
      fetchTradingStrategies();
      fetchStrategyAllocations();
    }
  }, [activeTab]);

  // Bot Allocation tab: load strategy allocations (empty-state hints) + bots in one pass.
  // fetchAllocations runs from the [bots] effect with skipGlobalLoading so we do not toggle
  // page-level loading (that was unmounting the panel and re-firing StrategyPreview fetches).
  useEffect(() => {
    if (activeTab !== 'allocation') return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        await fetchStrategyAllocations();
        if (cancelled) return;
        await fetchBots({ skipGlobalLoading: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  // Refresh accounts when deposit modal opens
  useEffect(() => {
    if (showDepositModal) {
      fetchAccounts();
    }
  }, [showDepositModal]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reconcile wallet balances whenever allocations change (client-side consistency)
  useEffect(() => {
    if (!walletBalance) return;
    const fiatAllocated = (strategyAllocations || []).reduce((sum: number, a: any) => sum + (a.amount || 0), 0);
    const cryptoAllocatedUsd = (strategyAllocations || []).reduce((sum: number, a: any) => sum + (a.usdValue || 0), 0);
    const allocatedTotal = fiatAllocated + cryptoAllocatedUsd;
    const available = Math.max((walletBalance.totalBalance || 0) - allocatedTotal, 0);
    if (
      Math.abs((walletBalance.allocatedBalance || 0) - allocatedTotal) > 0.009 ||
      Math.abs((walletBalance.availableBalance || 0) - available) > 0.009
    ) {
      setWalletBalance({
        ...walletBalance,
        allocatedBalance: allocatedTotal,
        availableBalance: available,
      });
    }
  }, [strategyAllocations]);

  // Auto-refresh analytics data when strategy section is expanded
  useEffect(() => {
    if (expandedStrategy !== null) {
      // Fetch immediately when section expands
      fetchStrategyAnalytics(expandedStrategy);
      
      // Set up interval to refresh every 10 seconds
      const interval = setInterval(() => {
        fetchStrategyAnalytics(expandedStrategy);
      }, 10000); // 10 seconds
      
      // Cleanup interval when section collapses
      return () => clearInterval(interval);
    }
  }, [expandedStrategy]);

  const isAllConsentGiven = Object.values(consent).every(Boolean);

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: '0 auto', 
      padding: isMobile ? '16px' : 20,
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: branding.primaryColor, marginBottom: 8 }}>
          Trading Bot Access
        </h1>
        <p style={{ color: '#666', margin: 0 }}>
          Manage your trading bot eligibility, consent, and allocations
        </p>
      </div>

      {/* Trading Account Balance Panel */}
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        padding: isMobile ? '16px' : '24px',
        marginBottom: 24,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        border: '2px solid #086afb'
      }}>
        {tradingAccountLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <FaSpinner className="animate-spin" style={{ fontSize: 24, color: branding.primaryColor }} />
            <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>Loading account...</div>
          </div>
        ) : tradingWalletSummary && tradingWalletSummary.totalBalance !== undefined ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Account Info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              paddingBottom: 16,
              borderBottom: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: '#086afb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <FaChartLine style={{ color: 'white', fontSize: 24 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: isMobile ? 18 : 20, color: '#0f172a', marginBottom: 4 }}>
                  Trading Account
                </div>
                <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#086afb', marginBottom: 4 }}>
                  {formatAmount(tradingWalletSummary.totalBalance || 0, tradingWalletSummary.currency || 'USD')}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
                  User ID: {profile?.id || 'N/A'}
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
              gap: isMobile ? 12 : 16
            }}>
              <div style={{
                background: '#f0fdf4',
                padding: isMobile ? '12px' : '16px',
                borderRadius: 12,
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ fontSize: 11, color: '#15803d', marginBottom: 4, fontWeight: 600 }}>
                  Total Deposited
                </div>
                <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#10b981' }}>
                  {formatAmount(
                    tradingWalletSummary.recentFundingEvents?.filter((e: any) => e.source === 'FIAT' || e.source === 'CRYPTO')
                      .reduce((sum: number, e: any) => sum + (parseFloat(e.amount) || 0), 0) || 0,
                    tradingWalletSummary.currency || 'USD'
                  )}
                </div>
              </div>
              <div style={{
                background: '#fef2f2',
                padding: isMobile ? '12px' : '16px',
                borderRadius: 12,
                border: '1px solid #fecaca'
              }}>
                <div style={{ fontSize: 11, color: '#991b1b', marginBottom: 4, fontWeight: 600 }}>
                  Total Withdrawn
                </div>
                <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#ef4444' }}>
                  {formatAmount(
                    tradingWalletSummary.recentFundingEvents?.filter((e: any) => e.source === 'TRANSFER')
                      .reduce((sum: number, e: any) => sum + (parseFloat(e.amount) || 0), 0) || 0,
                    tradingWalletSummary.currency || 'USD'
                  )}
                </div>
              </div>
              <div style={{
                background: '#faf5ff',
                padding: isMobile ? '12px' : '16px',
                borderRadius: 12,
                border: '1px solid #e9d5ff'
              }}>
                <div style={{ fontSize: 11, color: '#6b21a8', marginBottom: 4, fontWeight: 600 }}>
                  Allocated
                </div>
                <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#8b5cf6' }}>
                  {formatAmount(tradingWalletSummary.allocatedBalance || 0, tradingWalletSummary.currency || 'USD')}
                </div>
              </div>
              <div style={{
                background: tradingWalletSummary.availableBalance >= 0 ? '#f0fdf4' : '#fef2f2',
                padding: isMobile ? '12px' : '16px',
                borderRadius: 12,
                border: tradingWalletSummary.availableBalance >= 0 ? '1px solid #bbf7d0' : '1px solid #fecaca'
              }}>
                <div style={{
                  fontSize: 11,
                  color: tradingWalletSummary.availableBalance >= 0 ? '#15803d' : '#991b1b',
                  marginBottom: 4,
                  fontWeight: 600
                }}>
                  Available
                </div>
                <div style={{
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 700,
                  color: tradingWalletSummary.availableBalance >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {formatAmount(tradingWalletSummary.availableBalance || 0, tradingWalletSummary.currency || 'USD')}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <button
                onClick={() => setShowDepositModal(true)}
                style={{
                  flex: 1,
                  padding: isMobile ? '12px 16px' : '14px 24px',
                  background: '#086afb',
                  color: 'white',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all 0.2s',
                  minWidth: isMobile ? '100%' : 'auto'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#0759d4'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#086afb'}
              >
                <FaPlus /> Deposit
              </button>
              <button
                onClick={() => setShowWithdrawModal(true)}
                style={{
                  flex: 1,
                  padding: isMobile ? '12px 16px' : '14px 24px',
                  background: 'white',
                  color: '#086afb',
                  border: '2px solid #086afb',
                  borderRadius: 12,
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all 0.2s',
                  minWidth: isMobile ? '100%' : 'auto'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#086afb';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#086afb';
                }}
              >
                <FaArrowDown /> Withdraw
              </button>
            </div>
          {/* TrueLayer Fiat Top-Up History (TL-KC-US-005 / FE-US-017) */}
          {featureFlags.retail.enableTrueLayerKuCoinTopup && (
            <div style={{ marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
              <button
                type="button"
                onClick={() => {
                  const next = !showTopUpHistory;
                  setShowTopUpHistory(next);
                  if (next && topUpHistory.length === 0) fetchTopUpHistory(0);
                }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13, fontWeight: 600, color: '#086afb', padding: 0, marginBottom: 8
                }}
              >
                <span style={{ fontSize: 16 }}>{showTopUpHistory ? '▾' : '▸'}</span>
                Bank Top-Up History (TrueLayer)
              </button>

              {showTopUpHistory && (
                <div>
                  {topUpHistoryLoading ? (
                    <div style={{ textAlign: 'center', padding: 16, color: '#9ca3af', fontSize: 13 }}>
                      Loading…
                    </div>
                  ) : topUpHistory.length === 0 ? (
                    <div style={{
                      padding: 16, border: '1px solid #e5e7eb', borderRadius: 8,
                      background: '#f9fafb', color: '#6b7280', fontSize: 13, textAlign: 'center'
                    }}>
                      No bank top-ups found.
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            {['Date', 'Reference', 'Amount', 'Currency', 'Payment', 'Wallet Credit'].map(h => (
                              <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {topUpHistory.map((row: any) => {
                            const major = row.amountInMinor != null
                              ? (row.amountInMinor / 100).toFixed(2)
                              : '—';
                            const creditStatus: string = row.walletCreditStatus || 'PENDING';
                            const creditColor = creditStatus === 'CREDITED' ? '#0dca9d'
                              : creditStatus === 'FAILED' ? '#ff7171' : '#fbb445';
                            const payStatus: string = row.paymentStatus || '—';
                            const payColor = payStatus === 'settled' ? '#0dca9d'
                              : payStatus === 'failed' ? '#ff7171' : '#6b7280';
                            return (
                              <tr key={row.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '8px 10px', color: '#374151', whiteSpace: 'nowrap' }}>
                                  {row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-GB') : '—'}
                                </td>
                                <td style={{ padding: '8px 10px', color: '#6b7280', fontFamily: 'monospace', fontSize: 11 }}>
                                  {row.fundingReference || row.paymentId?.substring(0, 16) + '…' || '—'}
                                </td>
                                <td style={{ padding: '8px 10px', fontWeight: 600, color: '#086afb' }}>
                                  {major}
                                </td>
                                <td style={{ padding: '8px 10px', color: '#374151' }}>
                                  {row.currency || '—'}
                                </td>
                                <td style={{ padding: '8px 10px' }}>
                                  <span style={{
                                    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                                    background: `${payColor}18`, color: payColor, textTransform: 'capitalize'
                                  }}>
                                    {payStatus}
                                  </span>
                                </td>
                                <td style={{ padding: '8px 10px' }}>
                                  <span style={{
                                    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                                    background: `${creditColor}18`, color: creditColor
                                  }}>
                                    {creditStatus}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {topUpHistoryTotalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                          <button
                            disabled={topUpHistoryPage === 0}
                            onClick={() => fetchTopUpHistory(topUpHistoryPage - 1)}
                            style={{ padding: '4px 12px', border: '1px solid #e5e7eb', borderRadius: 6, cursor: topUpHistoryPage === 0 ? 'not-allowed' : 'pointer', background: 'white', fontSize: 12 }}
                          >
                            ‹ Prev
                          </button>
                          <span style={{ fontSize: 12, color: '#6b7280', padding: '4px 8px' }}>
                            Page {topUpHistoryPage + 1} of {topUpHistoryTotalPages}
                          </span>
                          <button
                            disabled={topUpHistoryPage >= topUpHistoryTotalPages - 1}
                            onClick={() => fetchTopUpHistory(topUpHistoryPage + 1)}
                            style={{ padding: '4px 12px', border: '1px solid #e5e7eb', borderRadius: 6, cursor: topUpHistoryPage >= topUpHistoryTotalPages - 1 ? 'not-allowed' : 'pointer', background: 'white', fontSize: 12 }}
                          >
                            Next ›
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
              Create a trading account to start trading!
            </div>
            <button
              onClick={() => setShowDepositModal(true)}
              style={{
                padding: '12px 24px',
                background: '#086afb',
                color: 'white',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <FaPlus /> Create Account
            </button>
          </div>
        )}
      </div>

      {/* Trading Account Modals */}
      {profile?.id && (
        <>
          <TradingAccountDepositModal
            isOpen={showDepositModal}
            onClose={() => setShowDepositModal(false)}
            onSuccess={() => {
              fetchTradingWalletSummary();
              fetchWalletBalance();
            }}
            accounts={accounts}
            tradingWallet={tradingWalletSummary}
            userId={profile.id}
          />
          <TradingAccountWithdrawModal
            isOpen={showWithdrawModal}
            onClose={() => setShowWithdrawModal(false)}
            onSuccess={() => {
              fetchTradingWalletSummary();
              fetchWalletBalance();
            }}
            accounts={accounts}
            tradingWallet={tradingWalletSummary}
            userId={profile.id}
          />
        </>
      )}

      {/* Tab Navigation */}
      <div style={{
        position: 'relative',
        marginBottom: 24,
        borderBottom: `2px solid ${branding.borderColor}`,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'visible'
      } as React.CSSProperties}>
        <div style={{
          display: 'flex',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          gap: '6px',
          flexWrap: 'wrap',
          rowGap: '6px'
        }}>
          {[
            { id: 'eligibility', label: 'Eligibility Check' },
            { id: 'consent', label: 'Consent & Terms' },
            { id: 'allocation', label: 'Bot Allocation' },
            { id: 'strategies', label: 'Trading Strategies' },
            { id: 'crypto-wallets', label: 'Crypto Wallets' },
            { id: 'kyt', label: 'Address Check' },
            { id: 'pnl-history', label: 'P&L History' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: isMobile ? '10px 8px' : '12px 20px',
                border: activeTab === tab.id 
                  ? `2px solid ${branding.primaryColor}` 
                  : `1px solid ${branding.borderColor}`,
                background: activeTab === tab.id ? branding.primaryColor : 'transparent',
                color: activeTab === tab.id ? 'white' : branding.textColor,
                cursor: 'pointer',
                borderRadius: '8px 8px 0 0',
                fontWeight: activeTab === tab.id ? 600 : 400,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexShrink: 0,
                flexGrow: 0,
                flexBasis: 'calc((100% - 12px) / 3)',
                fontSize: isMobile ? '12px' : '14px',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                minWidth: 'calc((100% - 12px) / 3)',
                maxWidth: 'calc((100% - 12px) / 3)',
                width: 'calc((100% - 12px) / 3)',
                boxSizing: 'border-box',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ background: '#fff', border: `1px solid ${branding.borderColor}`, borderRadius: 8, padding: 24 }}>
        
        {/* Eligibility Tab */}
        {activeTab === 'eligibility' && (
          <div>
            <h2 style={{ marginTop: 0, marginBottom: 16 }}>Eligibility Assessment</h2>
            
            {/* Debug section - remove in production */}
            {/* <div style={{ 
              marginBottom: 20, 
              padding: 12, 
              backgroundColor: '#f0f0f0', 
              borderRadius: 6, 
              fontSize: 12,
              border: '1px solid #ddd'
            }}>
              <strong>Debug - Current Eligibility State:</strong>
              <pre style={{ margin: '8px 0 0 0', fontSize: 11 }}>
                {JSON.stringify(eligibility, null, 2)}
              </pre>
            </div> */}
            
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div>Checking eligibility...</div>
              </div>
            ) : eligibility ? (
              <div>
                <div style={{ 
                  padding: 16, 
                  borderRadius: 8, 
                  marginBottom: 20,
                  backgroundColor: eligibility.isEligible ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${eligibility.isEligible ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: 12,
                    fontWeight: 600,
                    color: eligibility.isEligible ? '#155724' : '#721c24'
                  }}>
                    {eligibility.isEligible ? '✅' : '❌'} 
                    <span style={{ marginLeft: 8 }}>
                      {eligibility.isEligible ? 'Eligible for Trading Bots' : 'Not Eligible'}
                    </span>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Risk Score</div>
                      <div style={{ fontSize: 24, fontWeight: 600, color: branding.primaryColor }}>
                        {eligibility.riskScore}/100
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Max Allocation</div>
                      <div style={{ fontSize: 24, fontWeight: 600, color: branding.primaryColor }}>
                        ${eligibility.maxAllocation.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 style={{ marginBottom: 12 }}>Eligibility Criteria</h3>
                  <ul style={{ paddingLeft: 20 }}>
                    {eligibility.reasons.map((reason, index) => (
                      <li key={index} style={{ marginBottom: 8, color: '#28a745' }}>
                        ✅ {reason}
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{ marginTop: 20, textAlign: 'center' }}>
                  {eligibility.isEligible ? (
                    <button
                      onClick={() => setActiveTab('consent')}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: branding.primaryColor,
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 600
                      }}
                    >
                      Proceed to Consent
                    </button>
                  ) : (
                    <div>
                      <button
                        onClick={async () => {
                          const reason = prompt('Please provide a reason for manual review:');
                          if (reason && reason.trim()) {
                            try {
                              const userId = profile?.id;
                              if (!userId) {
                                alert('No user ID available for eligibility request');
                                return;
                              }
                              const response = await apiService.post(`/api/retail/trading/eligibility/request/${userId}`, {
                                reason: reason.trim()
                              });
                              if (response.success) {
                                alert('Eligibility request submitted successfully. An admin will review your request.');
                              } else {
                                alert('Failed to submit eligibility request. Please try again.');
                              }
                            } catch (error) {
                              console.error('Failed to submit eligibility request:', error);
                              alert('Failed to submit eligibility request. Please try again.');
                            }
                          }
                        }}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 16,
                          fontWeight: 600,
                          marginRight: '12px'
                        }}
                      >
                        Request Manual Review
                      </button>
                      <button
                        onClick={() => checkEligibility()}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          fontSize: 16,
                          fontWeight: 600
                        }}
                      >
                        Recheck Eligibility
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <button
                  onClick={checkEligibility}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: branding.primaryColor,
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 16
                  }}
                >
                  Check Eligibility
                </button>
              </div>
            )}
          </div>
        )}

        {/* Consent Tab */}
        {activeTab === 'consent' && (
          <div>
            <h2 style={{ marginTop: 0, marginBottom: 16 }}>Consent & Terms</h2>
            
            <div style={{ marginBottom: 24 }}>
              <p style={{ color: '#666', marginBottom: 20 }}>
                Please read and accept the following terms to proceed with trading bot access:
              </p>
              
              {/* Debug section - remove in production */}
              {/* <div style={{ 
                marginBottom: 20, 
                padding: 12, 
                backgroundColor: '#f0f0f0', 
                borderRadius: 6, 
                fontSize: 12,
                border: '1px solid #ddd'
              }}>
                <strong>Debug - Current Consent State:</strong>
                <pre style={{ margin: '8px 0 0 0', fontSize: 11 }}>
                  {JSON.stringify(consent, null, 2)}
                </pre>
              </div> */}

              <div style={{ display: 'grid', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={consent.riskAcknowledgment}
                    onChange={() => handleConsentChange('riskAcknowledgment')}
                    style={{ marginRight: 12, marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      Risk Acknowledgment
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>
                      I acknowledge that trading involves significant risk of loss and that past performance 
                      does not guarantee future results. I understand that I may lose some or all of my 
                      allocated funds.
                    </div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={consent.termsAccepted}
                    onChange={() => handleConsentChange('termsAccepted')}
                    style={{ marginRight: 12, marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      Terms of Service
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>
                      I have read and agree to the Trading Bot Terms of Service, including all 
                      applicable fees, limitations, and conditions.
                    </div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={consent.dataProcessingConsent}
                    onChange={() => handleConsentChange('dataProcessingConsent')}
                    style={{ marginRight: 12, marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      Data Processing Consent
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>
                      I consent to the processing of my trading data for bot optimization, 
                      risk management, and regulatory compliance purposes.
                    </div>
                  </div>
                </label>

                <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={consent.tradingConsent}
                    onChange={() => handleConsentChange('tradingConsent')}
                    style={{ marginRight: 12, marginTop: 2 }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      Trading Authorization
                    </div>
                    <div style={{ fontSize: 14, color: '#666' }}>
                      I authorize the trading bots to execute trades on my behalf within the 
                      allocated limits and risk parameters.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button
                onClick={() => setActiveTab('eligibility')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: branding.textColor,
                  border: `1px solid ${branding.borderColor}`,
                  borderRadius: 6,
                  cursor: 'pointer'
                }}
              >
                Back
              </button>
              <button
                onClick={submitConsent}
                disabled={!isAllConsentGiven || loading}
                style={{
                  padding: '12px 24px',
                  backgroundColor: isAllConsentGiven ? branding.primaryColor : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: isAllConsentGiven ? 'pointer' : 'not-allowed',
                  fontSize: 16,
                  fontWeight: 600
                }}
              >
                {loading ? 'Submitting...' : 'Submit Consent'}
              </button>
            </div>
          </div>
        )}

        {/* Allocation Tab */}
        {activeTab === 'allocation' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>Bot Allocation</h2>
              <button
                onClick={() => void fetchBots()}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: branding.primaryColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                {loading ? 'Loading...' : 'Refresh Bots'}
              </button>
            </div>
            
            {/* Debug section - remove in production */}
            {/* <div style={{ 
              marginBottom: 20, 
              padding: 12, 
              backgroundColor: '#f0f0f0', 
              borderRadius: 6, 
              fontSize: 12,
              border: '1px solid #ddd'
            }}>
              <strong>Debug - Current Allocations State:</strong>
              <pre style={{ margin: '8px 0 0 0', fontSize: 11 }}>
                {JSON.stringify(allocations, null, 2)}
              </pre>
            </div> */}
            
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div>Loading available bots...</div>
              </div>
            ) : bots.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, maxWidth: 560, margin: '0 auto' }}>
                <div style={{ fontSize: 18, marginBottom: 12, color: '#666' }}>
                  No trading bots available for allocation
                </div>
                <p style={{ color: '#777', fontSize: 14, lineHeight: 1.5, marginBottom: 16 }}>
                  This tab is for <strong>trading bots</strong> (the <code style={{ fontSize: 13 }}>trading_bot</code> catalogue).
                  Published <strong>trading strategies</strong> (for example Oracle-Aristos) are funded under{' '}
                  <strong>Trading Strategies</strong>, not here.
                </p>
                {strategyAllocations && strategyAllocations.length > 0 && (
                  <div
                    style={{
                      marginBottom: 20,
                      padding: 16,
                      backgroundColor: '#e8f4fd',
                      borderRadius: 8,
                      border: '1px solid #2196f3',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#1565c0', marginBottom: 8 }}>
                      You already have strategy allocation{strategyAllocations.length > 1 ? 's' : ''}
                    </div>
                    <ul style={{ margin: '0 0 12px 18px', color: '#444', fontSize: 14 }}>
                      {strategyAllocations.slice(0, 5).map((a: any) => (
                        <li key={a.id}>
                          {a.strategy?.name || 'Strategy'} — ${Number(a.amount || 0).toFixed(2)} ({a.status || '—'})
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={() => setActiveTab('strategies')}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: branding.primaryColor,
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      Open Trading Strategies
                    </button>
                  </div>
                )}
                <div style={{ color: '#999', fontSize: 14 }}>
                  If you expect classic bots here, ask your administrator to set <strong>enabled</strong> on the bot
                  (STOPPED bots still appear if enabled; FAILED bots are hidden) or create new ones.
                </div>
              </div>
            ) : (
              <div>
                <p style={{ color: '#666', marginBottom: 20 }}>
                  Allocate your funds across available trading bots. You can adjust allocations 
                  within your available balance.
                </p>

                {/* Wallet Balance Display */}
                {walletBalance && (
                  <div style={{ 
                    marginBottom: 20, 
                    padding: 16, 
                    backgroundColor: '#e3f2fd', 
                    borderRadius: 8,
                    border: '1px solid #2196f3'
                  }}>
                    <h3 style={{ margin: '0 0 12px 0', fontSize: 16, color: '#1976d2' }}>Wallet Balance</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                      <div>
                        <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Total Balance</div>
                        <div style={{ fontSize: 20, fontWeight: 600, color: '#1976d2' }}>
                          ${walletBalance.totalBalance.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Allocated</div>
                        <div style={{ fontSize: 20, fontWeight: 600, color: '#f57c00' }}>
                          ${walletBalance.allocatedBalance.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Available</div>
                        <div style={{ fontSize: 20, fontWeight: 600, color: '#388e3c' }}>
                          ${walletBalance.availableBalance.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Available Bots Summary */}
                <div style={{ 
                  marginBottom: 20, 
                  padding: 16, 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: 8,
                  border: `1px solid ${branding.borderColor}`
                }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>Available Trading Bots ({bots.length})</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    {bots.map(bot => (
                      <div key={bot.id} style={{ 
                        padding: 12, 
                        backgroundColor: 'white', 
                        borderRadius: 6,
                        border: `1px solid ${branding.borderColor}`
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{bot.name}</div>
                        <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                          {bot.symbol} • {bot.strategy}
                        </div>
                        <div style={{ fontSize: 12, color: '#999' }}>
                          Status: <span style={{ 
                            color: bot.status === 'RUNNING' ? 'green' : 'orange',
                            fontWeight: 600
                          }}>
                            {bot.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'grid', gap: 16 }}>
                  {bots.map(bot => {
                    // Find existing allocation for this bot, or create a default one
                    const existingAllocation = allocations.find(a => a.botId === bot.id);
                    const allocation = existingAllocation || {
                      botId: bot.id,
                      botName: bot.name,
                      symbol: bot.symbol,
                      strategy: bot.strategy,
                      userAllocation: 0,
                      maxAllocation: bot.maxNotionalUsd || 1000,
                      status: 'PENDING'
                    };
                    
                    return (
                    <div key={allocation.botId} style={{ 
                      border: `1px solid ${branding.borderColor}`, 
                      borderRadius: 8, 
                      padding: 16 
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: 18 }}>{allocation.botName}</h3>
                          <div style={{ fontSize: 14, color: '#666' }}>
                            {allocation.symbol} • {allocation.strategy}
                          </div>
                        </div>
                        <span style={{ 
                          padding: '4px 12px', 
                          borderRadius: 4, 
                          fontSize: 12,
                          backgroundColor: allocation.status === 'ACTIVE' ? '#d4edda' : 
                                         allocation.status === 'PENDING' ? '#fff3cd' : '#f8d7da',
                          color: allocation.status === 'ACTIVE' ? '#155724' : 
                                allocation.status === 'PENDING' ? '#856404' : '#721c24'
                        }}>
                          {allocation.status}
                        </span>
                      </div>

                      {/* Strategy Preview */}
                      <div style={{ marginBottom: 16 }}>
                        <StrategyPreview 
                          botId={allocation.botId}
                          botName={allocation.botName}
                          symbol={allocation.symbol}
                          strategyHint={allocation.strategy}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
                            Allocation Amount (USD)
                          </label>
                          <input
                            type="number"
                            value={allocation.userAllocation}
                            onChange={(e) => handleAllocationChange(allocation.botId, Number(e.target.value))}
                            min="0"
                            max={Math.min(allocation.maxAllocation, walletBalance?.availableBalance || 0)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: `1px solid ${branding.borderColor}`,
                              borderRadius: 4,
                              fontSize: 16
                            }}
                          />
                          <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                            Max: ${Math.min(allocation.maxAllocation, walletBalance?.availableBalance || 0).toLocaleString()}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 14, color: '#666' }}>Current</div>
                          <div style={{ fontSize: 20, fontWeight: 600, color: branding.primaryColor }}>
                            ${allocation.userAllocation.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>

                <div style={{ 
                  marginTop: 24, 
                  padding: 16, 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: 8,
                  border: `1px solid ${branding.borderColor}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Total Allocation:</span>
                    <span style={{ fontWeight: 600 }}>
                      ${bots.reduce((sum, bot) => {
                        const existingAllocation = allocations.find(a => a.botId === bot.id);
                        return sum + (existingAllocation?.userAllocation || 0);
                      }, 0).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#666', marginBottom: 8 }}>
                    <span>Available Balance:</span>
                    <span>${(walletBalance?.availableBalance || 0).toLocaleString()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: '#666' }}>
                    <span>Remaining After Allocation:</span>
                    <span style={{ 
                      color: (walletBalance?.availableBalance || 0) - bots.reduce((sum, bot) => {
                        const existingAllocation = allocations.find(a => a.botId === bot.id);
                        return sum + (existingAllocation?.userAllocation || 0);
                      }, 0) < 0 ? '#d32f2f' : '#388e3c',
                      fontWeight: 600
                    }}>
                      ${((walletBalance?.availableBalance || 0) - bots.reduce((sum, bot) => {
                        const existingAllocation = allocations.find(a => a.botId === bot.id);
                        return sum + (existingAllocation?.userAllocation || 0);
                      }, 0)).toLocaleString()}
                    </span>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: isMobile ? 'stretch' : 'space-between',
                  gap: isMobile ? 12 : 0,
                  marginTop: 24,
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <button
                    onClick={() => setActiveTab('consent')}
                    style={{
                      padding: isMobile ? '12px 16px' : '12px 24px',
                      backgroundColor: 'transparent',
                      color: branding.textColor,
                      border: `1px solid ${branding.borderColor}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: isMobile ? 14 : 16,
                      width: isMobile ? '100%' : 'auto',
                      flexShrink: isMobile ? 0 : 1
                    }}
                  >
                    Back
                  </button>
                  <div style={{ 
                    display: 'flex', 
                    gap: 12,
                    flexDirection: isMobile ? 'column' : 'row',
                    width: isMobile ? '100%' : 'auto',
                    flex: isMobile ? '1 1 auto' : '0 0 auto'
                  }}>
                    <button
                      onClick={() => navigate('/profit-analytics')}
                      style={{
                        padding: isMobile ? '12px 16px' : '12px 24px',
                        backgroundColor: 'transparent',
                        color: branding.primaryColor,
                        border: `1px solid ${branding.primaryColor}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: isMobile ? 14 : 16,
                        fontWeight: 600,
                        width: isMobile ? '100%' : 'auto',
                        flex: isMobile ? '1 1 auto' : '0 0 auto',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      View Analytics
                    </button>
                    <button
                      onClick={submitAllocations}
                      disabled={loading || (walletBalance?.availableBalance || 0) < allocations.reduce((sum, a) => sum + a.userAllocation, 0)}
                      style={{
                        padding: isMobile ? '12px 16px' : '12px 24px',
                        backgroundColor: (walletBalance?.availableBalance || 0) < allocations.reduce((sum, a) => sum + a.userAllocation, 0) ? '#ccc' : branding.primaryColor,
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        cursor: (walletBalance?.availableBalance || 0) < allocations.reduce((sum, a) => sum + a.userAllocation, 0) ? 'not-allowed' : 'pointer',
                        fontSize: isMobile ? 14 : 16,
                        fontWeight: 600,
                        width: isMobile ? '100%' : 'auto',
                        flex: isMobile ? '1 1 auto' : '0 0 auto',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {loading ? 'Submitting...' : 'Submit Allocations'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Trading Strategies Tab */}
        {activeTab === 'strategies' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>Trading Strategies</h2>
              <button
                onClick={() => {
                  fetchTradingStrategies();
                  fetchStrategyAllocations();
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: branding.primaryColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                Refresh
              </button>
            </div>

            {/* Wallet Balance */}
            {walletBalance && (
              <div style={{ 
                background: '#f8f9fa', 
                padding: 16, 
                borderRadius: 8, 
                marginBottom: 20,
                border: '1px solid #e9ecef',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>Wallet Balance</h3>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', 
                  gap: 16 
                }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Total Balance</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: branding.primaryColor }}>
                      ${walletBalance.totalBalance.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Allocated to Strategies</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#28a745' }}>
                      ${walletBalance.allocatedBalance.toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Available</div>
                    <div style={{ fontSize: 18, fontWeight: 600, color: '#17a2b8' }}>
                      ${walletBalance.availableBalance.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Strategy Overview */}
            <div style={{ marginBottom: 24, width: '100%', boxSizing: 'border-box' }}>
              <h3 style={{ marginBottom: 12 }}>Strategy Overview</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: 12,
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {/* Total Allocated to Strategies */}
                <div style={{ border: '1px solid #e9ecef', borderRadius: 8, padding: 12, background: '#fff' }}>
                  <div style={{ fontSize: 12, color: '#666' }}>Total Allocated to Strategies</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>
                    ${ (strategyAllocations || []).reduce((sum: number, a: any) => sum + (a.amount || 0), 0).toFixed(2) }
                  </div>
                </div>

                {/* Current Strategy P&L (sum of currentPnl) */}
                <div style={{ border: '1px solid #e9ecef', borderRadius: 8, padding: 12, background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#666' }}>Current Strategy P&amp;L</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: ((strategyAllocations || []).reduce((sum: number, a: any) => sum + (a.currentPnl || 0), 0) >= 0) ? '#198754' : '#dc3545' }}>
                      ${(strategyAllocations || []).reduce((sum: number, a: any) => sum + (a.currentPnl || 0), 0).toFixed(4)}
                    </div>
                  </div>
                  <Sparkline points={generateSparklineData('overview-total')} />
                </div>

                {/* Allocation Breakdown */}
                <div style={{ border: '1px solid #e9ecef', borderRadius: 8, padding: 12, background: '#fff' }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Allocation Breakdown</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 12, height: 12, backgroundColor: '#007bff', borderRadius: 2 }}></div>
                        <span style={{ fontSize: 11 }}>Fiat (USD)</span>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600 }}>
                        ${(strategyAllocations || []).reduce((sum: number, a: any) => sum + (a.amount || 0), 0).toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 12, height: 12, backgroundColor: '#28a745', borderRadius: 2 }}></div>
                        <span style={{ fontSize: 11 }}>Crypto</span>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600 }}>
                        ${(strategyAllocations || []).reduce((sum: number, a: any) => sum + (a.usdValue || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Available Strategies */}
            <div style={{ marginBottom: 24, width: '100%', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ margin: 0 }}>Available Strategies</h3>
                {userSubscription && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: 20,
                      padding: '4px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#1d4ed8'
                    }}>
                      📋 {userSubscription.planName} Plan
                    </span>
                    <span style={{
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: 20,
                      padding: '4px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      color: '#166534'
                    }}>
                      {strategyAllocations.filter((a: any) => a.status === 'ACTIVE').length} / {userSubscription.maxStrategies} bots enrolled
                    </span>
                  </div>
                )}
              </div>
              {tradingStrategies.length > 0 ? (
                <div style={{ display: 'grid', gap: 16, width: '100%', boxSizing: 'border-box' }}>
                  {tradingStrategies.map(strategy => {
                    const accessible = isStrategyAccessible(strategy);
                    return (
                    <div key={strategy.id} style={{ 
                      border: accessible ? '1px solid #e9ecef' : '1px solid #fca5a5',
                      borderRadius: 8, 
                      padding: 16,
                      background: accessible ? '#fff' : '#fef2f2',
                      width: '100%',
                      boxSizing: 'border-box',
                      overflowX: 'hidden',
                      opacity: accessible ? 1 : 0.85,
                      position: 'relative'
                    }}>
                      {/* Tier lock banner */}
                      {!accessible && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: '#fee2e2',
                          border: '1px solid #fca5a5',
                          borderRadius: 6,
                          padding: '8px 12px',
                          marginBottom: 12,
                          fontSize: 13,
                          color: '#991b1b',
                          fontWeight: 600
                        }}>
                          🔒 Requires <strong style={{ marginLeft: 4 }}>{strategy.requiredPlan}</strong> plan or higher.
                          {userSubscription
                            ? ` Your current plan: ${userSubscription.planName}.`
                            : ' You need an active subscription.'}
                        </div>
                      )}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: isMobile ? 'flex-start' : 'space-between', 
                        alignItems: isMobile ? 'flex-start' : 'flex-start', 
                        marginBottom: 12,
                        gap: isMobile ? 12 : 0,
                        width: '100%'
                      }}>
                        <div style={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <h4 style={{ margin: 0, fontSize: 16 }}>
                                {!accessible && <span style={{ marginRight: 4 }}>🔒</span>}
                                {strategy.name}
                              </h4>
                              {strategy.requiredPlan && (
                                <span style={{
                                  background: accessible ? '#dcfce7' : '#fee2e2',
                                  color: accessible ? '#166534' : '#991b1b',
                                  border: `1px solid ${accessible ? '#86efac' : '#fca5a5'}`,
                                  borderRadius: 12,
                                  padding: '2px 8px',
                                  fontSize: 11,
                                  fontWeight: 700,
                                  whiteSpace: 'nowrap'
                                }}>
                                  {strategy.requiredPlan}+
                                </span>
                              )}
                              {(() => {
                                const config = parseStrategyConfig(strategy.config || '{}');
                                if (config.paperTrading) {
                                  return (
                                    <span 
                                      style={{
                                        backgroundColor: '#ffc107',
                                        color: '#000',
                                        padding: '4px 10px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                      title="Paper Trading (Local Simulator): Uses real market data but simulates trade execution. No real money at risk."
                                    >
                                      📝 PAPER TRADING
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            {/* Mock/Real Data Toggle Switch */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>
                                {isMockData(strategy.id) ? 'Mock' : 'Real'}
                              </span>
                              <button
                                onClick={() => toggleMockDataMode(strategy.id)}
                                disabled={isPaperTradingStrategy(strategy.id) || hasRealMoneyLiveBots()}
                                style={{
                                  position: 'relative',
                                  width: 44,
                                  height: 24,
                                  borderRadius: 12,
                                  border: 'none',
                                  backgroundColor: isMockData(strategy.id) ? '#ffc107' : '#28a745',
                                  cursor: (isPaperTradingStrategy(strategy.id) || hasRealMoneyLiveBots()) ? 'not-allowed' : 'pointer',
                                  opacity: (isPaperTradingStrategy(strategy.id) || hasRealMoneyLiveBots()) ? 0.6 : 1,
                                  transition: 'background-color 0.2s',
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  outline: 'none'
                                }}
                                title={isPaperTradingStrategy(strategy.id)
                                  ? "Paper Trading: Always uses real market data (toggle disabled)"
                                  : hasRealMoneyLiveBots()
                                  ? "Toggle disabled: You have real money live bots. Cannot mix real and mock data."
                                  : (isMockData(strategy.id) 
                                    ? "Currently showing mock data. Click to switch to real data." 
                                    : "Currently showing real data. Click to switch to mock data.")}
                              >
                                <span style={{
                                  position: 'absolute',
                                  left: isMockData(strategy.id) ? 2 : 22,
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  backgroundColor: 'white',
                                  transition: 'left 0.2s',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                              </button>
                              <span style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>
                                {isMockData(strategy.id) ? 'Data' : 'Data'}
                              </span>
                            </div>
                          </div>
                          <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
                            Type: {strategy.strategyType.replace('_', ' ')}
                          </div>
                          <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
                            Status: <span style={{ 
                              color: strategy.status === 'ACTIVE' ? '#28a745' : 
                                    strategy.status === 'PAUSED' ? '#ffc107' : '#6c757d',
                              fontWeight: 600
                            }}>
                              {strategy.status}
                            </span>
                          </div>
                          {/* User Allocation for this strategy */}
                          {(() => {
                            // Find allocations for this strategy - check multiple possible ID fields
                            const allocationsForStrategy = (strategyAllocations || []).filter((a: any) => {
                              // Try different ways the strategy ID might be stored
                              const allocStrategyId = a.strategy?.id || a.strategyId || a.strategy_id;
                              const strategyIdNum = typeof strategy.id === 'number' ? strategy.id : parseInt(strategy.id);
                              const allocIdNum = typeof allocStrategyId === 'number' ? allocStrategyId : parseInt(allocStrategyId);
                              
                              return allocStrategyId === strategy.id || 
                                     allocStrategyId === strategy.id?.toString() ||
                                     allocStrategyId === strategyIdNum ||
                                     allocIdNum === strategyIdNum ||
                                     String(allocStrategyId) === String(strategy.id);
                            });
                            
                            const fiatAllocated = allocationsForStrategy.reduce((sum: number, a: any) => sum + (parseFloat(a.amount) || 0), 0);
                            const cryptoAllocatedUsd = allocationsForStrategy.reduce((sum: number, a: any) => sum + (parseFloat(a.usdValue) || 0), 0);
                            const totalAllocated = fiatAllocated + cryptoAllocatedUsd;
                            
                            // Always show if there are any allocations, even if they total 0
                            if (totalAllocated > 0 || allocationsForStrategy.length > 0) {
                              return (
                                <div 
                                  onClick={() => {
                                    setSelectedStrategyForAllocation(strategy.id);
                                    setStrategyAllocationAmount(String(totalAllocated.toFixed(2)));
                                    setStrategyAllocationNotes('');
                                    setStrategyCapacity(null);
                                    // Fetch strategy capacity when opening modal
                                    fetchStrategyCapacity(strategy.id);
                                    setShowStrategyAllocationModal(true);
                                  }}
                                  title="Click to modify your allocation"
                                  style={{ 
                                  fontSize: 14, 
                                  color: '#007bff', 
                                  marginBottom: 8,
                                  padding: '8px 12px',
                                  backgroundColor: '#e7f3ff',
                                  borderRadius: 6,
                                  border: '2px solid #007bff',
                                  fontWeight: 600,
                                  display: 'inline-block',
                                  cursor: 'pointer'
                                }}>
                                  💰 <strong>Your Allocation:</strong> <span style={{ color: branding.primaryColor, fontSize: 16 }}>${totalAllocated.toFixed(2)}</span>
                                  {fiatAllocated > 0 && cryptoAllocatedUsd > 0 && (
                                    <div style={{ fontSize: 11, color: '#666', marginTop: 2, fontWeight: 400 }}>
                                      ${fiatAllocated.toFixed(2)} USD + ${cryptoAllocatedUsd.toFixed(2)} crypto
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })()}
                          <StrategyExchangeInfo
                            sagemasterEnabled={sagemasterEnabled}
                            variant="badges"
                            style={{ marginBottom: 8, width: '100%', boxSizing: 'border-box' }}
                          />

                          {/* Strategy Metrics from config (if present) */}
                          {(() => {
                            const config = parseStrategyConfig(strategy.config || '{}');
                            const metricStyle: React.CSSProperties = { fontSize: 12, color: '#444', background: '#f8f9fa', padding: '6px 8px', borderRadius: 6 };
                            return (
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                                {config.leverage && <span style={metricStyle}>Leverage: <strong>{config.leverage}x</strong></span>}
                                {config.fixedOrderAmount && <span style={metricStyle}>Order: <strong>${Number(config.fixedOrderAmount).toLocaleString()}</strong></span>}
                                {config.takeProfit && <span style={metricStyle}>TP: <strong>{config.takeProfit}%</strong></span>}
                                {config.trailingStopLoss && <span style={metricStyle}>TSL: <strong>{config.trailingStopLoss}%</strong></span>}
                              </div>
                            );
                          })()}

                          {/* Paper Trading Notice */}
                          {(() => {
                            const config = parseStrategyConfig(strategy.config || '{}');
                            if (config.paperTrading) {
                              return (
                                <div style={{
                                  backgroundColor: '#fffbeb',
                                  border: '1px solid #fcd34d',
                                  borderRadius: '8px',
                                  padding: '12px 16px',
                                  marginBottom: '12px',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '12px'
                                }}>
                                  <span style={{ fontSize: '18px', flexShrink: 0 }}>📝</span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', marginBottom: '4px' }}>
                                      Paper Trading Mode (Local Simulator)
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#78350f', lineHeight: '1.5' }}>
                                      This strategy uses <strong>real market data</strong> from Kucoin but <strong>simulates trade execution locally</strong>. 
                                      No real money is at risk. All positions, P&L, and trades shown here are simulated.
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Mini P&L Sparkline with real data */}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 8, 
                            marginBottom: 4,
                            flexWrap: 'wrap',
                            width: '100%'
                          }}>
                            <span style={{ fontSize: 12, color: '#666' }}>P&amp;L trend</span>
                            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                              <Sparkline points={getRealSparklineData(strategy.id)} />
                            </div>
                            <button
                              onClick={() => {
                                if (expandedStrategy === strategy.id) {
                                  setExpandedStrategy(null);
                                } else {
                                  setExpandedStrategy(strategy.id);
                                  fetchStrategyAnalytics(strategy.id);
                                }
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: 10,
                                backgroundColor: expandedStrategy === strategy.id ? '#6c757d' : '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                flexShrink: 0
                              }}
                            >
                              {expandedStrategy === strategy.id ? 'Hide' : 'Details'}
                            </button>
                          </div>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          gap: 8,
                          flexDirection: isMobile ? 'column' : 'row',
                          width: isMobile ? '100%' : 'auto',
                          flexShrink: 0,
                          minWidth: 0
                        }}>
                          <button
                            onClick={() => {
                              setSelectedStrategyForAllocation(strategy.id);
                              setStrategyAllocationAmount('');
                              setStrategyAllocationNotes('');
                              setStrategyCapacity(null);
                              // Fetch strategy capacity when opening modal
                              fetchStrategyCapacity(strategy.id);
                              setShowStrategyAllocationModal(true);
                            }}
                            disabled={!accessible || strategy.status !== 'ACTIVE' || !walletBalance || walletBalance.availableBalance <= 0}
                            title={!accessible ? `Requires ${strategy.requiredPlan} plan or higher` : undefined}
                            style={{
                              padding: isMobile ? '10px 16px' : '8px 16px',
                              backgroundColor: !accessible ? '#6c757d'
                                : strategy.status === 'ACTIVE' && walletBalance && walletBalance.availableBalance > 0 
                                  ? branding.primaryColor : '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: 6,
                              cursor: (!accessible || strategy.status !== 'ACTIVE' || !walletBalance || walletBalance.availableBalance <= 0)
                                ? 'not-allowed' : 'pointer',
                              fontSize: 14,
                              fontWeight: 500,
                              width: isMobile ? '100%' : 'auto',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {!accessible ? `🔒 ${strategy.requiredPlan}+ Only` : 'Allocate USD'}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedStrategyForCryptoAllocation(strategy.id);
                              setShowCryptoStrategyAllocationModal(true);
                            }}
                            disabled={strategy.status !== 'ACTIVE' || userCryptoWallets.length === 0}
                            style={{
                              padding: isMobile ? '10px 16px' : '8px 16px',
                              backgroundColor: strategy.status === 'ACTIVE' && userCryptoWallets.length > 0 
                                ? '#28a745' : '#6c757d',
                              color: 'white',
                              border: 'none',
                              borderRadius: 6,
                              cursor: strategy.status === 'ACTIVE' && userCryptoWallets.length > 0 
                                ? 'pointer' : 'not-allowed',
                              fontSize: 14,
                              fontWeight: 500,
                              width: isMobile ? '100%' : 'auto',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Allocate Crypto
                          </button>
                        </div>
                      </div>
                      
                      {/* Strategy Description */}
                      <div style={{ fontSize: 14, color: '#666', lineHeight: 1.5, width: '100%', wordBreak: 'break-word' }}>
                        {strategy.strategyType === 'MEME_COIN_PAIRS' && (
                          <div>
                            <strong>Meme Coin Pairs Strategy:</strong> This strategy trades pairs of meme coins simultaneously - 
                            going LONG on one coin and SHORT on another. It profits from relative price movements 
                            between the coins, making it market-neutral. Perfect for volatile meme coin markets.
                          </div>
                        )}
                      </div>

                      {/* Expanded Analytics Section */}
                      {expandedStrategy === strategy.id && (
                        <div style={{ 
                          marginTop: 16, 
                          padding: 16, 
                          backgroundColor: '#f8f9fa', 
                          borderRadius: 8,
                          border: '1px solid #e9ecef',
                          overflowX: 'hidden',
                          width: '100%',
                          boxSizing: 'border-box'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Strategy Analytics</h4>
                              {/* Mock/Real Data Toggle Switch */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 10, color: '#666', fontWeight: 500 }}>
                                  {isMockData(strategy.id) ? 'Mock' : 'Real'}
                                </span>
                                <button
                                  onClick={() => toggleMockDataMode(strategy.id)}
                                  disabled={isPaperTradingStrategy(strategy.id) || hasRealMoneyLiveBots()}
                                  style={{
                                    position: 'relative',
                                    width: 40,
                                    height: 20,
                                    borderRadius: 10,
                                    border: 'none',
                                    backgroundColor: isMockData(strategy.id) ? '#ffc107' : '#28a745',
                                    cursor: (isPaperTradingStrategy(strategy.id) || hasRealMoneyLiveBots()) ? 'not-allowed' : 'pointer',
                                    opacity: (isPaperTradingStrategy(strategy.id) || hasRealMoneyLiveBots()) ? 0.6 : 1,
                                    transition: 'background-color 0.2s',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    outline: 'none'
                                  }}
                                  title={isPaperTradingStrategy(strategy.id)
                                    ? "Paper Trading: Always uses real market data (toggle disabled)"
                                    : hasRealMoneyLiveBots()
                                    ? "Toggle disabled: You have real money live bots. Cannot mix real and mock data."
                                    : (isMockData(strategy.id) 
                                      ? "Currently showing mock data. Click to switch to real data." 
                                      : "Currently showing real data. Click to switch to mock data.")}
                                >
                                  <span style={{
                                    position: 'absolute',
                                    left: isMockData(strategy.id) ? 2 : 18,
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: 'white',
                                    transition: 'left 0.2s',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                  }} />
                                </button>
                                <span style={{ fontSize: 10, color: '#666', fontWeight: 500 }}>
                                  Data
                                </span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                onClick={() => openFullAnalytics(strategy.id, strategy.name || `Strategy ${strategy.id}`)}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  backgroundColor: 'transparent',
                                  color: '#086afb',
                                  border: '1px solid #086afb',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}
                                title="Open full analytics with KuCoin Live / DB data source toggle"
                              >
                                📊 Full Analytics
                              </button>
                              <button
                                onClick={() => openHistoryModal(strategy.id)}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  backgroundColor: '#086afb',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z"/>
                                </svg>
                                History
                              </button>
                            </div>
                          </div>
                          
                          {/* Paper Trading Notice */}
                          {(() => {
                            const config = parseStrategyConfig(strategy.config || '{}');
                            if (config.paperTrading) {
                              return (
                                <div style={{
                                  backgroundColor: '#fffbeb',
                                  border: '1px solid #fcd34d',
                                  borderRadius: '8px',
                                  padding: '12px 16px',
                                  marginBottom: '16px',
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: '12px'
                                }}>
                                  <span style={{ fontSize: '18px', flexShrink: 0 }}>📝</span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#92400e', marginBottom: '4px' }}>
                                      Paper Trading Mode (Local Simulator)
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#78350f', lineHeight: '1.5' }}>
                                      This strategy uses <strong>real market data</strong> from Kucoin but <strong>simulates trade execution locally</strong>. 
                                      No real money is at risk. All positions, P&L, and trades shown here are simulated.
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}
                          
                          {/* Recent Trades */}
                          {strategyTrades[strategy.id] && strategyTrades[strategy.id].length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                              <h5 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>Recent Trades</h5>
                              <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                                {strategyTrades[strategy.id].slice(0, 5).map((trade: any, index: number) => {
                                  const formatExitReason = (reason: string) => {
                                    if (!reason) return '';
                                    const reasonMap: {[key: string]: string} = {
                                      'TAKE_PROFIT': 'Take Profit',
                                      'STOP_LOSS': 'Stop Loss',
                                      'TRAILING_STOP': 'Trailing Stop Loss',
                                      'TRAILING_STOP_LOSS': 'Trailing Stop Loss',
                                      'BASKET_PROFIT': 'Basket TP (2%)',
                                      'BASKET_TP': 'Basket TP (2%)',
                                      'UNIVERSAL_BASKET_PROFIT': 'Universal Basket TP',
                                      'REBALANCE': 'Rebalance',
                                      'MANUAL': 'Manual Close',
                                      'LONG_STOP_LOSS': 'Stop Loss',
                                      'SHORT_STOP_LOSS': 'Stop Loss',
                                      'LONG_TAKE_PROFIT': 'Take Profit',
                                      'SHORT_TAKE_PROFIT': 'Take Profit',
                                      'LONG_TRAILING_STOP_LOSS': 'Trailing Stop Loss',
                                      'SHORT_TRAILING_STOP_LOSS': 'Trailing Stop Loss'
                                    };
                                    return reasonMap[reason] || reason;
                                  };
                                  
                                  const formatDateTime = (dateString: string) => {
                                    if (!dateString) return '';
                                    try {
                                      const date = new Date(dateString);
                                      return date.toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false
                                      });
                                    } catch {
                                      return '';
                                    }
                                  };
                                  
                                  const getPositionColor = (side: string) => {
                                    if (!side) return '#666';
                                    const upperSide = side.toUpperCase();
                                    if (upperSide === 'LONG' || upperSide.includes('LONG')) return '#198754';
                                    if (upperSide === 'SHORT' || upperSide.includes('SHORT')) return '#dc3545';
                                    return '#666';
                                  };
                                  
                                  const getPositionBg = (side: string) => {
                                    if (!side) return 'transparent';
                                    const upperSide = side.toUpperCase();
                                    if (upperSide === 'LONG' || upperSide.includes('LONG')) return '#d1f4e0';
                                    if (upperSide === 'SHORT' || upperSide.includes('SHORT')) return '#ffe0e0';
                                    return 'transparent';
                                  };
                                  
                                  return (
                                    <div key={index} style={{ 
                                      display: 'flex', 
                                      flexDirection: 'column',
                                      padding: '8px',
                                      marginBottom: '6px',
                                      background: '#ffffff',
                                      border: '1px solid #e9ecef',
                                      borderRadius: 6
                                    }}>
                                      {/* First row: Symbol, Side, Time */}
                                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                          <span style={{ fontSize: 12, fontWeight: 700, color: '#1a202c' }}>
                                            {trade.symbol || 'N/A'}
                                          </span>
                                          {trade.side && (
                                            <span style={{ 
                                              fontSize: 9, 
                                              fontWeight: 700,
                                              color: getPositionColor(trade.side),
                                              background: getPositionBg(trade.side),
                                              padding: '2px 6px',
                                              borderRadius: 4
                                            }}>
                                              {trade.side.toUpperCase()}
                                            </span>
                                          )}
                                        </div>
                                        {(trade.timestamp || trade.closedAt || trade.createdAt || trade.entryTime || trade.exitTime) && (
                                          <div style={{ fontSize: 10, color: '#888', marginLeft: 'auto' }}>
                                            {formatDateTime(trade.timestamp || trade.closedAt || trade.createdAt || trade.exitTime || trade.entryTime)}
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Second row: Exit reason (if closed) */}
                                      {trade.exitReason && trade.side === 'CLOSE' && (
                                        <div style={{ marginBottom: 4 }}>
                                          <div style={{ 
                                            fontSize: 10, 
                                            color: '#086afb',
                                            fontWeight: 600,
                                            background: '#e0f2fe',
                                            padding: '3px 6px',
                                            borderRadius: 4,
                                            display: 'inline-block'
                                          }}>
                                            ✓ Closed: {formatExitReason(trade.exitReason)}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Third row: Amount, Price, PnL */}
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ fontSize: 10, color: '#666' }}>
                                          {trade.side === 'CLOSE' && trade.entryPrice ? (() => {
                                            const amount = Number(trade.amount) || 0;
                                            const entryPrice = Number(trade.entryPrice) || 0;
                                            const exitPrice = Number(trade.price) || 0;
                                            // Calculate notional value at exit: Position Size × (Exit Price / Entry Price)
                                            const notionalValueAtExit = entryPrice > 0 ? amount * (exitPrice / entryPrice) : 0;
                                            return (
                                              <span>
                                                ${amount.toFixed(0)} @ Entry: ${entryPrice.toFixed(6)} → Exit: ${exitPrice.toFixed(6)} (Value: ${notionalValueAtExit.toFixed(2)})
                                              </span>
                                            );
                                          })() : (
                                            <span>
                                              ${(Number(trade.amount) || 0).toFixed(0)} @ ${(Number(trade.price) || 0).toFixed(6)}
                                            </span>
                                          )}
                                        </div>
                                        <div style={{ 
                                          fontSize: 12, 
                                          fontWeight: 700,
                                          color: (Number(trade.pnl) || 0) >= 0 ? '#198754' : '#dc3545'
                                        }}>
                                          {(() => {
                                            const pnl = Number(trade.pnl) || 0;
                                            // Show more decimal places for small values (< $0.01)
                                            if (Math.abs(pnl) < 0.01 && pnl !== 0) {
                                              return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(6);
                                            }
                                            return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(2);
                                          })()}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Open Positions */}
                          {strategyPositions[strategy.id] && strategyPositions[strategy.id].length > 0 && (
                            <div style={{ marginBottom: 16 }}>
                              <h5 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>Open Positions</h5>
                              <div style={{ maxHeight: 120, overflowY: 'auto' }}>
                                {strategyPositions[strategy.id].map((position: any, index: number) => {
                                  const formatDateTime = (dateString: string) => {
                                    if (!dateString) return '';
                                    try {
                                      const date = new Date(dateString);
                                      return date.toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false
                                      });
                                    } catch {
                                      return '';
                                    }
                                  };
                                  
                                  return (
                                    <div key={index} style={{ 
                                      display: 'flex', 
                                      alignItems: 'center',
                                      padding: '4px 0',
                                      borderBottom: index < strategyPositions[strategy.id].length - 1 ? '1px solid #e9ecef' : 'none'
                                    }}>
                                      <div style={{ fontSize: 11, marginRight: '12px' }}>
                                        <span style={{ fontWeight: 600 }}>{position.symbol || 'N/A'}</span> {position.side || 'N/A'}
                                      </div>
                                      {position.createdAt && (
                                        <div style={{ fontSize: 10, color: '#888', marginRight: '12px' }}>
                                          {formatDateTime(position.createdAt)}
                                        </div>
                                      )}
                                      <div style={{ fontSize: 11, color: '#666', marginRight: '12px' }}>
                                        ${(Number(position.amount) || 0).toFixed(0)} @ ${(Number(position.entryPrice) || 0).toFixed(6)}
                                      </div>
                                      <div style={{ 
                                        fontSize: 11, 
                                        fontWeight: 600,
                                        color: (Number(position.unrealizedPnl) || 0) >= 0 ? '#198754' : '#dc3545',
                                        marginLeft: 'auto'
                                      }}>
                                        {(() => {
                                          const pnl = Number(position.unrealizedPnl) || 0;
                                          // Show more decimal places for small values (< $0.01)
                                          if (Math.abs(pnl) < 0.01 && pnl !== 0) {
                                            return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(4);
                                          }
                                          return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(2);
                                        })()}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* P&L History Chart */}
                          {strategyPnlHistory[strategy.id] && strategyPnlHistory[strategy.id].length > 0 && (
                            <div>
                              <h5 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>30-Day P&L History</h5>
                              <div style={{ height: 80, display: 'flex', alignItems: 'center' }}>
                                <Sparkline 
                                  points={strategyPnlHistory[strategy.id].map((entry: any) => parseFloat(entry.cumulativePnl) || 0)} 
                                  width={300} 
                                  height={60}
                                  color="#007bff"
                                />
                              </div>
                              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                {(() => {
                                  // Calculate Total P&L = Realized P&L (from history) + Unrealized P&L (from open positions)
                                  const realizedPnl = Number(strategyPnlHistory[strategy.id][strategyPnlHistory[strategy.id].length - 1]?.cumulativePnl) || 0;
                                  const unrealizedPnl = (strategyPositions[strategy.id] || []).reduce((sum: number, position: any) => {
                                    return sum + (Number(position.unrealizedPnl) || 0);
                                  }, 0);
                                  const totalPnl = realizedPnl + unrealizedPnl;
                                  return (
                                    <div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                        <span>Strategy P&L: <strong style={{ color: totalPnl >= 0 ? '#198754' : '#dc3545' }}>${totalPnl.toFixed(2)}</strong></span>
                                        <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>
                                          (from strategy trades & positions)
                                        </span>
                                        <span 
                                          style={{ 
                                            cursor: 'pointer', 
                                            color: '#086afb', 
                                            fontSize: 11,
                                            textDecoration: 'underline',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const infoDiv = (e.currentTarget as HTMLElement).parentElement?.nextElementSibling as HTMLElement;
                                            if (infoDiv) {
                                              infoDiv.style.display = infoDiv.style.display === 'none' ? 'block' : 'none';
                                            }
                                          }}
                                        >
                                          ℹ️ Info
                                        </span>
                                      </div>
                                      <div style={{ 
                                        display: 'none', 
                                        marginTop: 8, 
                                        padding: 10, 
                                        backgroundColor: '#f8f9fa', 
                                        borderRadius: 6, 
                                        fontSize: 11, 
                                        lineHeight: 1.5,
                                        border: '1px solid #e9ecef'
                                      }}>
                                        <div style={{ fontWeight: 600, marginBottom: 6, color: '#333' }}>Strategy P&L Breakdown:</div>
                                        <div style={{ marginBottom: 4 }}>
                                          <strong>Strategy P&L = Realized P&L + Unrealized P&L</strong>
                                        </div>
                                        <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                          • <strong>Realized P&L:</strong> ${realizedPnl.toFixed(2)} (from closed trades)
                                        </div>
                                        <div style={{ marginLeft: 8, marginBottom: 6 }}>
                                          • <strong>Unrealized P&L:</strong> ${unrealizedPnl.toFixed(2)} (from open positions)
                                        </div>
                                        <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontStyle: 'italic', paddingTop: 6, borderTop: '1px solid #dee2e6' }}>
                                          Note: This is strategy-level P&L calculated from strategy trades and positions only. Account-level P&L (including all positions and fees) is available in the admin dashboard.
                                        </div>
                                        <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 4, color: '#333', paddingTop: 6, borderTop: '1px solid #dee2e6' }}>
                                          Current Strategy Value:
                                        </div>
                                        <div style={{ fontSize: 12, color: '#666' }}>
                                          Current Value = Total Investment + Total P&L
                                        </div>
                                        <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                                          Note: "Order: $X" is the trade size, not total investment. Total investment is the sum of all user allocations.
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                          
                          {/* Show Total P&L even if no history but there are positions or trades */}
                          {(!strategyPnlHistory[strategy.id] || strategyPnlHistory[strategy.id].length === 0) && 
                           ((strategyPositions[strategy.id] && strategyPositions[strategy.id].length > 0) || 
                            (strategyTrades[strategy.id] && strategyTrades[strategy.id].length > 0)) && (
                            <div>
                              <h5 style={{ margin: '0 0 8px 0', fontSize: 12, fontWeight: 600, color: '#666' }}>30-Day P&L History</h5>
                              <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                {(() => {
                                  // Calculate Total P&L from trades and open positions
                                  const realizedPnl = (strategyTrades[strategy.id] || []).reduce((sum: number, trade: any) => {
                                    return sum + (Number(trade.pnl) || 0);
                                  }, 0);
                                  const unrealizedPnl = (strategyPositions[strategy.id] || []).reduce((sum: number, position: any) => {
                                    return sum + (Number(position.unrealizedPnl) || 0);
                                  }, 0);
                                  const totalPnl = realizedPnl + unrealizedPnl;
                                  return (
                                    <div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                        <span>Strategy P&L: <strong style={{ color: totalPnl >= 0 ? '#198754' : '#dc3545' }}>${totalPnl.toFixed(2)}</strong></span>
                                        <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>
                                          (from strategy trades & positions)
                                        </span>
                                        <span 
                                          style={{ 
                                            cursor: 'pointer', 
                                            color: '#086afb', 
                                            fontSize: 11,
                                            textDecoration: 'underline',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const infoDiv = (e.currentTarget as HTMLElement).parentElement?.nextElementSibling as HTMLElement;
                                            if (infoDiv) {
                                              infoDiv.style.display = infoDiv.style.display === 'none' ? 'block' : 'none';
                                            }
                                          }}
                                        >
                                          ℹ️ Info
                                        </span>
                                      </div>
                                      <div style={{ 
                                        display: 'none', 
                                        marginTop: 8, 
                                        padding: 10, 
                                        backgroundColor: '#f8f9fa', 
                                        borderRadius: 6, 
                                        fontSize: 11, 
                                        lineHeight: 1.5,
                                        border: '1px solid #e9ecef'
                                      }}>
                                        <div style={{ fontWeight: 600, marginBottom: 6, color: '#333' }}>Strategy P&L Breakdown:</div>
                                        <div style={{ marginBottom: 4 }}>
                                          <strong>Strategy P&L = Realized P&L + Unrealized P&L</strong>
                                        </div>
                                        <div style={{ marginLeft: 8, marginBottom: 4 }}>
                                          • <strong>Realized P&L:</strong> ${realizedPnl.toFixed(2)} (from closed trades)
                                        </div>
                                        <div style={{ marginLeft: 8, marginBottom: 6 }}>
                                          • <strong>Unrealized P&L:</strong> ${unrealizedPnl.toFixed(2)} (from open positions)
                                        </div>
                                        <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontStyle: 'italic', paddingTop: 6, borderTop: '1px solid #dee2e6' }}>
                                          Note: This is strategy-level P&L calculated from strategy trades and positions only. Account-level P&L (including all positions and fees) is available in the admin dashboard.
                                        </div>
                                        <div style={{ fontWeight: 600, marginTop: 8, marginBottom: 4, color: '#333', paddingTop: 6, borderTop: '1px solid #dee2e6' }}>
                                          Current Strategy Value:
                                        </div>
                                        <div style={{ fontSize: 12, color: '#666' }}>
                                          Current Value = Total Investment + Total P&L
                                        </div>
                                        <div style={{ fontSize: 11, color: '#999', marginTop: 4, fontStyle: 'italic' }}>
                                          Note: "Order: $X" is the trade size, not total investment. Total investment is the sum of all user allocations.
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ); })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                  <p>No trading strategies available at the moment.</p>
                  <p style={{ fontSize: 14 }}>Check back later for new strategies.</p>
                </div>
              )}
            </div>

            {/* User's Strategy Allocations */}
            <div>
              <h3 style={{ marginBottom: 16 }}>Your Strategy Allocations</h3>
              {strategyAllocations.length > 0 ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  {strategyAllocations.map(allocation => (
                    <div key={allocation.id} style={{ 
                      border: '1px solid #e9ecef', 
                      borderRadius: 8, 
                      padding: 16,
                      background: '#fff',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: isMobile ? 'flex-start' : 'space-between', 
                        alignItems: isMobile ? 'flex-start' : 'center',
                        gap: isMobile ? 12 : 0
                      }}>
                        <div style={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                          <div style={{ fontWeight: 600, marginBottom: 4, wordBreak: 'break-word' }}>
                            {allocation.strategy?.name || 'Unknown Strategy'}
                          </div>
                          <div style={{ fontSize: 14, color: '#666' }}>
                            Allocated: ${allocation.amount.toFixed(2)}
                          </div>
                          <div style={{ fontSize: 14, color: '#666' }}>
                            Status: <span style={{ 
                              color: allocation.status === 'ACTIVE' ? '#28a745' : 
                                    allocation.status === 'PENDING' ? '#ffc107' : '#dc3545',
                              fontWeight: 600
                            }}>
                              {allocation.status === 'CANCELLED' && allocation.currentPnl && allocation.currentPnl <= -allocation.amount ? 
                                'De-enrolled (Losses reached investment)' : allocation.status}
                            </span>
                          </div>
                          {allocation.status === 'CANCELLED' && allocation.currentPnl && allocation.currentPnl <= -allocation.amount && (
                            <div style={{ fontSize: 12, color: '#dc3545', marginTop: 4, fontStyle: 'italic' }}>
                              You were automatically de-enrolled as losses reached your investment amount
                            </div>
                          )}
                          {allocation.notes && (
                            <div style={{ fontSize: 14, color: '#666', marginTop: 4, wordBreak: 'break-word' }}>
                              Notes: {allocation.notes}
                            </div>
                          )}
                        </div>
                        <div style={{ 
                          textAlign: isMobile ? 'left' : 'right',
                          flexShrink: 0,
                          width: isMobile ? '100%' : 'auto'
                        }}>
                          <div style={{ 
                            fontSize: 16, 
                            fontWeight: 600, 
                            color: allocation.currentPnl && allocation.currentPnl < 0 ? '#dc3545' : 
                                   allocation.currentPnl && allocation.currentPnl > 0 ? '#28a745' : branding.primaryColor
                          }}>
                            P&L: ${allocation.currentPnl ? allocation.currentPnl.toFixed(4) : '0.0000'}
                            {allocation.currentPnl && allocation.currentPnl <= -allocation.amount && (
                              <span style={{ fontSize: 12, marginLeft: 4, color: '#dc3545' }}>(Capped)</span>
                            )}
                          </div>
                          <div style={{ fontSize: 14, color: '#666' }}>
                            Allocated: {new Date(allocation.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>
                  <p>You haven't allocated funds to any strategies yet.</p>
                  <p style={{ fontSize: 14 }}>Choose a strategy above to get started.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'crypto-wallets' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0 }}>Crypto Wallet Trading</h2>
              <button
                onClick={() => {
                  fetchUserCryptoWallets();
                  fetchCryptoTradingSummary();
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: branding.primaryColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                Refresh
              </button>
            </div>

            {/* Crypto Wallet Summary */}
            <div style={{
              background: '#f8f9fa',
              border: `1px solid ${branding.borderColor}`,
              borderRadius: 8,
              padding: 16,
              marginBottom: 24
            }}>
              <h3 style={{ margin: '0 0 12px 0', color: branding.textColor }}>Your Crypto Wallets</h3>
              {userCryptoWallets.length > 0 ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  {userCryptoWallets.map((wallet: any) => (
                    <div key={wallet.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: 12,
                      background: '#fff',
                      borderRadius: 6,
                      border: '1px solid #dee2e6'
                    }}>
                      <div>
                        <strong>{wallet.blockchain}</strong>
                        <div style={{ fontSize: 12, color: '#6c757d' }}>
                          {wallet.address?.substring(0, 8)}...{wallet.address?.substring(wallet.address.length - 6)}
                        </div>
                        <div style={{ fontSize: 14, color: '#28a745' }}>
                          Balance: {wallet.balance || 'N/A'} {wallet.blockchain}
                        </div>
                      </div>
                      <button 
                        onClick={() => {
                          setSelectedWalletForAllocation(wallet);
                          setSelectedBotForCrypto(null);
                          setShowCryptoAllocationModal(true);
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer'
                        }}
                      >
                        Allocate to Bots
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6c757d', margin: 0 }}>
                  No crypto wallets found. Create wallets in the Crypto Portfolio page first.
                </p>
              )}
            </div>

            {/* Current Crypto Allocations */}
            <div style={{
              background: '#f8f9fa',
              border: `1px solid ${branding.borderColor}`,
              borderRadius: 8,
              padding: 16,
              marginBottom: 24
            }}>
              <h3 style={{ margin: '0 0 12px 0', color: branding.textColor }}>Current Crypto Allocations</h3>
              {cryptoAllocations.length > 0 ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  {cryptoAllocations.map((allocation: any) => (
                    <div key={allocation.id} style={{
                      padding: 12,
                      background: '#fff',
                      borderRadius: 6,
                      border: '1px solid #dee2e6',
                      marginBottom: 12
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div>
                          <strong>Bot: {allocation.botName || `Bot ${allocation.botId}`}</strong>
                          <div style={{ fontSize: 12, color: '#6c757d' }}>
                            Wallet: {allocation.walletAddress?.substring(0, 8)}...{allocation.walletAddress?.substring(allocation.walletAddress.length - 6)}
                          </div>
                          <div style={{ fontSize: 14, color: '#28a745' }}>
                            {allocation.allocatedCryptoAmount} {allocation.allocatedCryptoCurrency} (${allocation.allocatedUsdValue})
                          </div>
                        </div>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          fontSize: 12,
                          backgroundColor: allocation.allocationStatus === 'ACTIVE' ? '#d4edda' : '#f8d7da',
                          color: allocation.allocationStatus === 'ACTIVE' ? '#155724' : '#721c24'
                        }}>
                          {allocation.allocationStatus}
                        </span>
                      </div>

                      {/* Strategy Preview */}
                      <div style={{ marginTop: 12 }}>
                        <StrategyPreview 
                          botId={allocation.botId}
                          botName={allocation.botName || `Bot ${allocation.botId}`}
                          symbol={allocation.symbol || 'BTC/USDT'}
                          strategyHint={allocation.strategy || bots.find((b) => b.id === allocation.botId)?.strategy}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6c757d', margin: 0 }}>
                  No crypto allocations found. Allocate your crypto wallets to trading bots to get started.
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setActiveTab('allocation')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: branding.primaryColor,
                  border: `1px solid ${branding.primaryColor}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 600
                }}
              >
                Back to USD Allocation
              </button>
              <button
                onClick={() => navigate('/crypto')}
                style={{
                  padding: '12px 24px',
                  backgroundColor: branding.primaryColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 16,
                  fontWeight: 600
                }}
              >
                Manage Crypto Wallets
              </button>
            </div>
          </div>
        )}

        {/* KYT Tab */}
        {activeTab === 'kyt' && (
          <div>
            <h2 style={{ marginTop: 0, marginBottom: 16 }}>Address Risk Check</h2>
            <p style={{ color: '#666', marginBottom: 24 }}>
              Check the risk level and compliance status of any cryptocurrency address before trading
            </p>
            <RetailKYT />
          </div>
        )}

        {/* TL-KC-EPIC-2: P&L Distribution History */}
        {activeTab === 'pnl-history' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h2 style={{ marginTop: 0, marginBottom: 4 }}>P&L Distribution History</h2>
                <p style={{ color: '#666', marginBottom: 0, fontSize: 14 }}>
                  Your proportional share of trading profits and losses distributed from the shared KuCoin pool.
                  {pnlHistoryTotalElements > 0 && ` ${pnlHistoryTotalElements} distribution event(s) total.`}
                </p>
              </div>
              <button
                onClick={() => fetchPnlHistory(0)}
                disabled={pnlHistoryLoading}
                style={{ padding: '8px 16px', background: branding.primaryColor, color: 'white',
                  border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
              >
                {pnlHistoryLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>

            {pnlHistoryLoading && (
              <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Loading P&L history…</div>
            )}

            {!pnlHistoryLoading && pnlHistory.length === 0 && (
              <div style={{ textAlign: 'center', padding: 40, background: '#f8f9fa',
                borderRadius: 12, border: '1px dashed #dee2e6' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
                <h3 style={{ margin: '0 0 8px', color: '#495057' }}>No P&L distributions yet</h3>
                <p style={{ color: '#6c757d', margin: 0, fontSize: 14 }}>
                  P&L from shared KuCoin trading is distributed proportionally to your wallet balance every 6 hours.
                  Distributions appear here once the engine runs and your strategy has realized P&L.
                </p>
              </div>
            )}

            {!pnlHistoryLoading && pnlHistory.length > 0 && (
              <div>
                {/* Summary cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 12, marginBottom: 20 }}>
                  {[
                    {
                      label: 'Total Distributed',
                      value: '$' + pnlHistory.reduce((s: number, e: any) => s + (e.distributedAmountUsd || 0), 0).toFixed(2),
                      color: '#28a745'
                    },
                    {
                      label: 'Distribution Events',
                      value: pnlHistoryTotalElements.toString(),
                      color: branding.primaryColor
                    },
                    {
                      label: 'Latest Distribution',
                      value: pnlHistory[0]?.createdAt ? new Date(pnlHistory[0].createdAt).toLocaleDateString() : '—',
                      color: '#6c757d'
                    }
                  ].map(card => (
                    <div key={card.label} style={{ background: '#fff', border: '1px solid #e9ecef',
                      borderRadius: 10, padding: '16px 20px' }}>
                      <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4 }}>{card.label}</div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: card.color }}>{card.value}</div>
                    </div>
                  ))}
                </div>

                {/* Distribution table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                        {['Date', 'Strategy', 'Your Share', 'Pool P&L', 'Distributed to You', 'Balance After'].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#495057' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pnlHistory.map((event: any, idx: number) => {
                        const isPositive = (event.distributedAmountUsd || 0) >= 0;
                        return (
                          <tr key={event.id || idx} style={{
                            borderBottom: '1px solid #f0f0f0',
                            background: idx % 2 === 0 ? '#fff' : '#fafafa'
                          }}>
                            <td style={{ padding: '10px 12px', color: '#6c757d' }}>
                              {event.createdAt ? new Date(event.createdAt).toLocaleString() : '—'}
                            </td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{ background: '#e9ecef', borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                                Strategy #{event.strategyId}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', color: '#495057' }}>
                              {event.shareFraction
                                ? (parseFloat(event.shareFraction) * 100).toFixed(4) + '%'
                                : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', fontWeight: 500,
                              color: (event.strategyRealizedPnlUsd || 0) >= 0 ? '#28a745' : '#dc3545' }}>
                              {(event.strategyRealizedPnlUsd || 0) >= 0 ? '+' : ''}
                              ${parseFloat(event.strategyRealizedPnlUsd || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: '10px 12px', fontWeight: 700,
                              color: isPositive ? '#28a745' : '#dc3545' }}>
                              {isPositive ? '+' : ''}${parseFloat(event.distributedAmountUsd || 0).toFixed(2)}
                            </td>
                            <td style={{ padding: '10px 12px', color: '#495057' }}>
                              ${parseFloat(event.walletBalanceAfterUsd || 0).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pnlHistoryTotalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                    <button
                      disabled={pnlHistoryPage === 0 || pnlHistoryLoading}
                      onClick={() => fetchPnlHistory(pnlHistoryPage - 1)}
                      style={{ padding: '6px 14px', border: '1px solid #dee2e6', borderRadius: 6,
                        background: '#fff', cursor: 'pointer' }}
                    >← Prev</button>
                    <span style={{ padding: '6px 12px', color: '#6c757d', fontSize: 13 }}>
                      Page {pnlHistoryPage + 1} of {pnlHistoryTotalPages}
                    </span>
                    <button
                      disabled={pnlHistoryPage >= pnlHistoryTotalPages - 1 || pnlHistoryLoading}
                      onClick={() => fetchPnlHistory(pnlHistoryPage + 1)}
                      style={{ padding: '6px 14px', border: '1px solid #dee2e6', borderRadius: 6,
                        background: '#fff', cursor: 'pointer' }}
                    >Next →</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Crypto Wallet Allocation Modal */}
      {showCryptoAllocationModal && selectedWalletForAllocation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Allocate Crypto Wallet to Bot</h2>
              <button
                onClick={() => {
                  setShowCryptoAllocationModal(false);
                  setSelectedWalletForAllocation(null);
                  setSelectedBotForCrypto(null);
                  setAllocationAmount('');
                  setAllocationNotes('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Selected Wallet:</strong> {selectedWalletForAllocation.blockchain} - {selectedWalletForAllocation.address?.substring(0, 8)}...{selectedWalletForAllocation.address?.substring(selectedWalletForAllocation.address.length - 6)}
            </div>
            
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Select Trading Bot:</label>
              <select 
                value={selectedBotForCrypto || ''}
                onChange={(e) => setSelectedBotForCrypto(parseInt(e.target.value))}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">Choose a bot...</option>
                {bots.map((bot: any) => (
                  <option key={bot.id} value={bot.id}>
                    {bot.name} ({bot.symbol}) - {bot.strategy}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Allocation Amount ({selectedWalletForAllocation.blockchain}):</label>
              <input
                type="number"
                placeholder="Enter amount to allocate"
                value={allocationAmount}
                onChange={(e) => setAllocationAmount(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <small style={{ color: '#6c757d', fontSize: '12px' }}>
                Available balance: {selectedWalletForAllocation.balance || 'N/A'} {selectedWalletForAllocation.blockchain}
              </small>
            </div>
            
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Notes (Optional):</label>
              <textarea
                placeholder="Add any notes about this allocation..."
                value={allocationNotes}
                onChange={(e) => setAllocationNotes(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '60px' }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={handleCryptoWalletAllocation}
                disabled={loading || !selectedBotForCrypto || !allocationAmount}
                style={{
                  padding: '12px 24px',
                  background: (loading || !selectedBotForCrypto || !allocationAmount) ? '#6c757d' : '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (loading || !selectedBotForCrypto || !allocationAmount) ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  flex: 1
                }}
              >
                {loading ? 'Allocating...' : 'Allocate Crypto Wallet'}
              </button>
              <button
                onClick={() => {
                  setShowCryptoAllocationModal(false);
                  setSelectedWalletForAllocation(null);
                  setSelectedBotForCrypto(null);
                  setAllocationAmount('');
                  setAllocationNotes('');
                }}
                style={{
                  padding: '12px 24px',
                  background: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Allocation Modal */}
      {showStrategyAllocationModal && selectedStrategyForAllocation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Allocate Funds to Strategy</h2>
              <button
                onClick={() => {
                  setShowStrategyAllocationModal(false);
                  setSelectedStrategyForAllocation(null);
                  setStrategyAllocationAmount('');
                  setStrategyAllocationNotes('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>
                {tradingStrategies.find(s => s.id === selectedStrategyForAllocation)?.name}
              </h3>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                Allocate funds to this trading strategy. Your funds will be used to participate in the strategy's trades.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Allocation Amount (USD) *
              </label>
              <input
                type="number"
                value={strategyAllocationAmount}
                onChange={(e) => setStrategyAllocationAmount(e.target.value)}
                placeholder="Enter amount to allocate"
                min="1"
                max={Math.min(
                  walletBalance?.availableBalance || 0,
                  strategyCapacity?.remainingCapacity || 0
                )}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '16px'
                }}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {walletBalance && (
                  <div>
                    Your Available Balance: <strong>${walletBalance.availableBalance.toFixed(2)}</strong>
                  </div>
                )}
                {loadingCapacity ? (
                  <div style={{ color: '#999' }}>Loading capacity information...</div>
                ) : strategyCapacity ? (
                  <>
                    <div>
                      Strategy Capacity: <strong>${strategyCapacity.maxInvestment.toFixed(2)}</strong> (Currently Allocated: ${strategyCapacity.currentAllocated.toFixed(2)})
                    </div>
                    {strategyCapacity.isFull ? (
                      <div style={{ color: '#dc3545', fontWeight: 600 }}>
                        ⚠️ Strategy is full. No further investments are allowed.
                      </div>
                    ) : (
                      <div style={{ color: '#28a745', fontWeight: 600 }}>
                        ✓ Remaining Capacity: <strong>${strategyCapacity.remainingCapacity.toFixed(2)}</strong>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Notes (Optional)
              </label>
              <textarea
                value={strategyAllocationNotes}
                onChange={(e) => setStrategyAllocationNotes(e.target.value)}
                placeholder="Add any notes about this allocation..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowStrategyAllocationModal(false);
                  setSelectedStrategyForAllocation(null);
                  setStrategyAllocationAmount('');
                  setStrategyAllocationNotes('');
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={allocateToStrategy}
                disabled={Boolean(
                  loading || 
                  !strategyAllocationAmount || 
                  parseFloat(strategyAllocationAmount) <= 0 ||
                  (strategyCapacity?.isFull === true) ||
                  (strategyCapacity !== null && parseFloat(strategyAllocationAmount) > (strategyCapacity.remainingCapacity || 0))
                )}
                style={{
                  padding: '12px 24px',
                  backgroundColor: Boolean(
                    loading || 
                    !strategyAllocationAmount || 
                    parseFloat(strategyAllocationAmount) <= 0 ||
                    (strategyCapacity?.isFull === true) ||
                    (strategyCapacity !== null && parseFloat(strategyAllocationAmount) > (strategyCapacity.remainingCapacity || 0))
                  ) ? '#ccc' : branding.primaryColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: Boolean(
                    loading || 
                    !strategyAllocationAmount || 
                    parseFloat(strategyAllocationAmount) <= 0 ||
                    (strategyCapacity?.isFull === true) ||
                    (strategyCapacity !== null && parseFloat(strategyAllocationAmount) > (strategyCapacity.remainingCapacity || 0))
                  ) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                {loading ? 'Allocating...' : 'Allocate Funds'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Crypto Strategy Allocation Modal */}
      {showCryptoStrategyAllocationModal && selectedStrategyForCryptoAllocation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>
              Allocate Crypto to Strategy
            </h3>

            {/* Strategy Selection */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Selected Strategy
              </label>
              <div style={{
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                {tradingStrategies.find(s => s.id === selectedStrategyForCryptoAllocation)?.name || 'Unknown Strategy'}
              </div>
            </div>

            {/* Crypto Wallet Selection */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Select Crypto Wallet
              </label>
              <select
                value={selectedWalletForStrategyAllocation?.address || ''}
                onChange={(e) => {
                  const wallet = userCryptoWallets.find(w => w.address === e.target.value);
                  setSelectedWalletForStrategyAllocation(wallet);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '16px'
                }}
              >
                <option value="">Select a crypto wallet</option>
                {userCryptoWallets.map(wallet => (
                  <option key={wallet.address} value={wallet.address}>
                    {wallet.blockchain} - {wallet.address.substring(0, 8)}...{wallet.address.substring(wallet.address.length - 6)} 
                    (Balance: {wallet.balance?.toFixed(6) || '0.000000'})
                  </option>
                ))}
              </select>
            </div>

            {/* Crypto Amount */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Crypto Amount
              </label>
              <input
                type="number"
                step="0.000001"
                value={cryptoStrategyAllocationAmount}
                onChange={(e) => setCryptoStrategyAllocationAmount(e.target.value)}
                placeholder="Enter crypto amount"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '16px'
                }}
              />
              {selectedWalletForStrategyAllocation && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Available: {selectedWalletForStrategyAllocation.balance?.toFixed(6) || '0.000000'} {selectedWalletForStrategyAllocation.blockchain}
                </div>
              )}
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Notes (Optional)
              </label>
              <textarea
                value={cryptoStrategyAllocationNotes}
                onChange={(e) => setCryptoStrategyAllocationNotes(e.target.value)}
                placeholder="Add any notes about this allocation"
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '16px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCryptoStrategyAllocationModal(false);
                  setSelectedStrategyForCryptoAllocation(null);
                  setSelectedWalletForStrategyAllocation(null);
                  setCryptoStrategyAllocationAmount('');
                  setCryptoStrategyAllocationNotes('');
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={allocateCryptoToStrategy}
                disabled={loading || !cryptoStrategyAllocationAmount || !selectedWalletForStrategyAllocation || parseFloat(cryptoStrategyAllocationAmount) <= 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: loading || !cryptoStrategyAllocationAmount || !selectedWalletForStrategyAllocation || parseFloat(cryptoStrategyAllocationAmount) <= 0 
                    ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !cryptoStrategyAllocationAmount || !selectedWalletForStrategyAllocation || parseFloat(cryptoStrategyAllocationAmount) <= 0 
                    ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                {loading ? 'Allocating...' : 'Allocate Crypto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trading History Modal */}
      {showHistoryModal && historyStrategyId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                Trading History - {tradingStrategies.find(s => s.id === historyStrategyId)?.name || 'Strategy'}
              </h3>
                  {historyStrategyId && (() => {
                    const strategy = tradingStrategies.find(s => s.id === historyStrategyId);
                    if (strategy) {
                      const config = parseStrategyConfig(strategy.config || '{}');
                      if (config.paperTrading) {
                        return (
                          <span 
                            style={{
                              backgroundColor: '#ffc107',
                              color: '#000',
                              padding: '6px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Paper Trading (Local Simulator): Uses real market data but simulates trade execution. No real money at risk."
                          >
                            📝 PAPER TRADING
                          </span>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>
                {historyStrategyId && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>
                      {isMockData(historyStrategyId) ? 'Mock' : 'Real'}
                    </span>
                    <button
                      onClick={() => {
                        toggleMockDataMode(historyStrategyId);
                        fetchAllHistoryTrades(historyStrategyId);
                      }}
                      disabled={isPaperTradingStrategy(historyStrategyId) || hasRealMoneyLiveBots()}
                      style={{
                        position: 'relative',
                        width: 44,
                        height: 24,
                        borderRadius: 12,
                        border: 'none',
                        backgroundColor: isMockData(historyStrategyId) ? '#ffc107' : '#28a745',
                        cursor: (isPaperTradingStrategy(historyStrategyId) || hasRealMoneyLiveBots()) ? 'not-allowed' : 'pointer',
                        opacity: (isPaperTradingStrategy(historyStrategyId) || hasRealMoneyLiveBots()) ? 0.6 : 1,
                        transition: 'background-color 0.2s',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        outline: 'none'
                      }}
                      title={isPaperTradingStrategy(historyStrategyId)
                        ? "Paper Trading: Always uses real market data (toggle disabled)"
                        : hasRealMoneyLiveBots()
                        ? "Toggle disabled: You have real money live bots. Cannot mix real and mock data."
                        : (isMockData(historyStrategyId) 
                          ? "Currently showing mock data. Click to switch to real data." 
                          : "Currently showing real data. Click to switch to mock data.")}
                    >
                      <span style={{
                        position: 'absolute',
                        left: isMockData(historyStrategyId) ? 2 : 22,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        transition: 'left 0.2s',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }} />
                    </button>
                    <span style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>
                      Data
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setHistoryStrategyId(null);
                  setAllHistoryTrades([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: 0,
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Filters */}
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              backgroundColor: '#f8f9fa'
            }}>
              {/* Date Range Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Period:</label>
                <select
                  value={historyDateRange}
                  onChange={(e) => {
                    setHistoryDateRange(e.target.value);
                    setHistoryPage(1);
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="7D">Last 7 Days</option>
                  <option value="30D">Last 30 Days</option>
                  <option value="90D">Last 90 Days</option>
                  <option value="ALL">All Time</option>
                </select>
              </div>

              {/* Coin Filter */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>Coin:</label>
                <select
                  value={historyCoinFilter}
                  onChange={(e) => {
                    setHistoryCoinFilter(e.target.value);
                    setHistoryPage(1);
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="ALL">All Coins</option>
                  {Array.from(new Set(allHistoryTrades.map((t: any) => t.symbol).filter(Boolean))).sort().map((coin: string) => (
                    <option key={coin} value={coin}>{coin}</option>
                  ))}
                </select>
              </div>

              {/* Results Count */}
              <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center' }}>
                {(() => {
                  const filtered = filterHistoryTrades();
                  return `${filtered.length} trade${filtered.length !== 1 ? 's' : ''}`;
                })()}
              </div>
            </div>

            {/* Trades List */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px'
            }}>
              {historyLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  Loading history...
                </div>
              ) : (() => {
                const filtered = filterHistoryTrades();
                const paginated = filtered.slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize);
                
                if (paginated.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      No trades found for the selected filters.
                    </div>
                  );
                }

                return (
                  <>
                    {paginated.map((trade: any, index: number) => {
                      const formatExitReason = (reason: string) => {
                        if (!reason) return '';
                        const reasonMap: {[key: string]: string} = {
                          'TAKE_PROFIT': 'Take Profit',
                          'STOP_LOSS': 'Stop Loss',
                          'TRAILING_STOP': 'Trailing Stop Loss',
                          'TRAILING_STOP_LOSS': 'Trailing Stop Loss',
                          'BASKET_PROFIT': 'Basket TP',
                          'BASKET_TP': 'Basket TP',
                          'UNIVERSAL_BASKET_PROFIT': 'Universal Basket TP',
                          'REBALANCE': 'Rebalance',
                          'MANUAL': 'Manual Close',
                          'LONG_STOP_LOSS': 'Stop Loss',
                          'SHORT_STOP_LOSS': 'Stop Loss',
                          'LONG_TAKE_PROFIT': 'Take Profit',
                          'SHORT_TAKE_PROFIT': 'Take Profit',
                          'LONG_TRAILING_STOP_LOSS': 'Trailing Stop Loss',
                          'SHORT_TRAILING_STOP_LOSS': 'Trailing Stop Loss'
                        };
                        return reasonMap[reason] || reason;
                      };
                      
                      const formatDateTime = (dateString: string) => {
                        if (!dateString) return '';
                        try {
                          const date = new Date(dateString);
                          return date.toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          });
                        } catch {
                          return '';
                        }
                      };
                      
                      const getPositionColor = (side: string) => {
                        if (!side) return '#666';
                        const upperSide = side.toUpperCase();
                        if (upperSide === 'LONG' || upperSide === 'BUY') return '#198754';
                        if (upperSide === 'SHORT' || upperSide === 'SELL') return '#dc3545';
                        if (upperSide === 'CLOSE') return '#086afb';
                        return '#666';
                      };
                      
                      const getPositionBg = (side: string) => {
                        if (!side) return 'transparent';
                        const upperSide = side.toUpperCase();
                        if (upperSide === 'LONG' || upperSide === 'BUY') return '#d1f4e0';
                        if (upperSide === 'SHORT' || upperSide === 'SELL') return '#ffe0e0';
                        if (upperSide === 'CLOSE') return '#e0f2fe';
                        return 'transparent';
                      };

                      return (
                        <div key={index} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          padding: '12px',
                          marginBottom: '8px',
                          background: '#ffffff',
                          border: '1px solid #e9ecef',
                          borderRadius: '6px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '14px', fontWeight: 700, color: '#1a202c' }}>
                                {trade.symbol || 'N/A'}
                              </span>
                              {trade.side && (
                                <span style={{
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  color: getPositionColor(trade.side),
                                  background: getPositionBg(trade.side),
                                  padding: '3px 8px',
                                  borderRadius: '4px'
                                }}>
                                  {trade.side.toUpperCase()}
                                </span>
                              )}
                              {trade.exitReason && trade.side === 'CLOSE' && (
                                <span style={{
                                  fontSize: '10px',
                                  color: '#086afb',
                                  fontWeight: 600,
                                  background: '#e0f2fe',
                                  padding: '3px 8px',
                                  borderRadius: '4px'
                                }}>
                                  ✓ {formatExitReason(trade.exitReason)}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '12px', color: '#888' }}>
                              {formatDateTime(trade.timestamp || trade.closedAt || trade.createdAt || trade.entryTime || trade.exitTime)}
                            </div>
                          </div>
                          
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {trade.side === 'CLOSE' && trade.entryPrice ? (() => {
                                const amount = Number(trade.amount) || 0;
                                const entryPrice = Number(trade.entryPrice) || 0;
                                const exitPrice = Number(trade.price) || 0;
                                // Calculate notional value at exit: Position Size × (Exit Price / Entry Price)
                                const notionalValueAtExit = entryPrice > 0 ? amount * (exitPrice / entryPrice) : 0;
                                return (
                                  <span>
                                    ${amount.toFixed(0)} @ Entry: ${entryPrice.toFixed(6)} → Exit: ${exitPrice.toFixed(6)} (Value: ${notionalValueAtExit.toFixed(2)})
                                  </span>
                                );
                              })() : (
                                <span>
                                  ${(Number(trade.amount) || 0).toFixed(0)} @ ${(Number(trade.price || trade.entryPrice) || 0).toFixed(6)}
                                </span>
                              )}
                            </div>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: 700,
                              color: (Number(trade.pnl) || 0) >= 0 ? '#198754' : '#dc3545'
                            }}>
                              {(() => {
                                const pnl = Number(trade.pnl) || 0;
                                // Show more decimal places for small values (< $0.01)
                                if (Math.abs(pnl) < 0.01 && pnl !== 0) {
                                  return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(6);
                                }
                                return (pnl >= 0 ? '+' : '') + '$' + pnl.toFixed(2);
                              })()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>

            {/* Pagination */}
            {(() => {
              const filtered = filterHistoryTrades();
              const totalPages = Math.ceil(filtered.length / historyPageSize);
              
              if (totalPages <= 1) return null;

              return (
                <div style={{
                  padding: '16px 20px',
                  borderTop: '1px solid #e9ecef',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#f8f9fa'
                }}>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Page {historyPage} of {totalPages}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                      disabled={historyPage === 1}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: historyPage === 1 ? '#f5f5f5' : 'white',
                        cursor: historyPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        color: historyPage === 1 ? '#999' : '#333'
                      }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setHistoryPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={historyPage === totalPages}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: historyPage === totalPages ? '#f5f5f5' : 'white',
                        cursor: historyPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        color: historyPage === totalPages ? '#999' : '#333'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── Full Analytics Modal (KuCoin Live / DB Rollback toggle) ── */}
      {showFullAnalytics && fullAnalyticsStrategyId && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex',
          alignItems: 'flex-start', justifyContent: 'center',
          zIndex: 1100, padding: '20px', overflowY: 'auto'
        }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowFullAnalytics(false); }}
        >
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 900,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            marginTop: 20
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              flexWrap: 'wrap', gap: 10,
              padding: '16px 20px', borderBottom: '1px solid #e9ecef', position: 'sticky',
              top: 0, background: '#fff', zIndex: 10
            }}>
              <div style={{ minWidth: 0, flex: '1 1 180px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#212529', wordBreak: 'break-word' }}>
                  📊 Full Analytics — {fullAnalyticsStrategyName}
                </div>
                <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                  Switch between KuCoin live data and DB data below
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
                {/* Data source toggle */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  background: '#f8f9fa', border: '1px solid #dee2e6',
                  borderRadius: 8, padding: '4px 6px'
                }}>
                  <span style={{ fontSize: 12, color: '#6c757d', fontWeight: 500 }}>Data:</span>
                  <button
                    onClick={() => {
                      setFullAnalyticsDataSource('kucoin');
                      fetchRetailFullAnalytics(fullAnalyticsStrategyId, 'kucoin');
                    }}
                    style={{
                      padding: '8px 12px', minHeight: 36, borderRadius: 6, border: 0, cursor: 'pointer',
                      fontSize: 12, fontWeight: 600,
                      background: fullAnalyticsDataSource === 'kucoin' ? '#086afb' : 'transparent',
                      color: fullAnalyticsDataSource === 'kucoin' ? '#fff' : '#495057',
                      transition: 'all 0.15s'
                    }}
                    title="Use live KuCoin exchange data (more accurate)"
                  >KuCoin Live</button>
                  <button
                    onClick={() => {
                      setFullAnalyticsDataSource('db');
                      fetchRetailFullAnalytics(fullAnalyticsStrategyId, 'db');
                    }}
                    style={{
                      padding: '8px 12px', minHeight: 36, borderRadius: 6, border: 0, cursor: 'pointer',
                      fontSize: 12, fontWeight: 600,
                      background: fullAnalyticsDataSource === 'db' ? '#6c757d' : 'transparent',
                      color: fullAnalyticsDataSource === 'db' ? '#fff' : '#495057',
                      transition: 'all 0.15s'
                    }}
                    title="Rollback to DB-sourced data"
                  >DB (Rollback)</button>
                </div>
                <button
                  onClick={() => setShowFullAnalytics(false)}
                  style={{
                    background: 'none', border: '1px solid #dee2e6', borderRadius: 6,
                    fontSize: 16, cursor: 'pointer', color: '#6c757d',
                    width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0
                  }}
                  title="Close"
                >✕</button>
              </div>
            </div>

            <div style={{ padding: '16px 20px' }}>
              {/* Error banner */}
              {fullAnalyticsError && (
                <div style={{
                  padding: '10px 14px', background: '#fff3cd', border: '1px solid #ffc107',
                  borderRadius: 6, fontSize: 13, color: '#664d03', marginBottom: 16
                }}>
                  ⚠️ {fullAnalyticsError}
                </div>
              )}

              {/* Loading */}
              {fullAnalyticsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                  <svg className="animate-spin" style={{ width: 32, height: 32, color: '#adb5bd' }} fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              ) : (
                <>
                  {/* KuCoin summary bar */}
                  {fullAnalyticsDataSource === 'kucoin' && fullAnalyticsKucoinSummary && (
                    <div style={{
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                      gap: 12, marginBottom: 20, padding: 14,
                      background: '#f0f7ff', border: '1px solid #b8d4f8', borderRadius: 8
                    }}>
                      {[
                        { label: 'Closed Positions', value: fullAnalyticsKucoinSummary.closedPositions, isNum: true },
                        { label: 'Total P&L', value: `$${Number(fullAnalyticsKucoinSummary.totalPnl).toFixed(2)}`, color: Number(fullAnalyticsKucoinSummary.totalPnl) >= 0 ? '#198754' : '#dc3545' },
                        { label: 'Trade Fees', value: `$${Number(fullAnalyticsKucoinSummary.totalTradeFee).toFixed(2)}`, color: '#dc3545' },
                        { label: 'Funding Fees', value: `$${Number(fullAnalyticsKucoinSummary.totalFundingFee).toFixed(2)}`, color: '#dc3545' },
                        { label: 'Net P&L', value: `$${Number(fullAnalyticsKucoinSummary.netPnl).toFixed(2)}`, color: Number(fullAnalyticsKucoinSummary.netPnl) >= 0 ? '#198754' : '#dc3545' },
                        { label: 'Win Rate', value: `${Number(fullAnalyticsKucoinSummary.winRate).toFixed(1)}%`, color: '#086afb' },
                      ].map((item, i) => (
                        <div key={i} style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 2 }}>{item.label}</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: (item as any).color || '#212529' }}>
                            {item.isNum ? item.value : item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!fullAnalyticsLoading && (
                    <TradeEvaluationPanel
                      trades={mapTradesForEvaluation(fullAnalyticsTrades, fullAnalyticsDataSource)}
                      fixedOrderAmount={fullAnalyticsOrderSize}
                      defaultCount={12}
                    />
                  )}

                  {/* Trades table */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#212529', marginBottom: 10 }}>
                      {fullAnalyticsDataSource === 'kucoin' ? 'Closed Trades (KuCoin)' : 'Recent Trades (DB)'}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                            <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Symbol</th>
                            <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Side</th>
                            {fullAnalyticsDataSource === 'kucoin' ? (
                              <>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Size</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Entry</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Exit</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>P&L</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Fees</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Closed</th>
                              </>
                            ) : (
                              <>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Amount</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Price</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>P&L</th>
                                <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Date</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {fullAnalyticsTrades.length > 0 ? fullAnalyticsTrades.map((trade: any, i: number) => {
                            const pnl = parseFloat(trade.pnl ?? trade.realizedPnl ?? 0);
                            const fees = parseFloat(trade.tradeFee ?? 0) + parseFloat(trade.fundingFee ?? 0);
                            return (
                              <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                <td style={{ padding: '6px 8px', fontWeight: 500 }}>{trade.symbol}</td>
                                <td style={{ padding: '6px 8px' }}>
                                  <span style={{
                                    fontSize: 11, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                                    background: (trade.side === 'LONG' || trade.side === 'BUY') ? '#d1e7dd' : '#f8d7da',
                                    color: (trade.side === 'LONG' || trade.side === 'BUY') ? '#0f5132' : '#842029'
                                  }}>{trade.side}</span>
                                </td>
                                {fullAnalyticsDataSource === 'kucoin' ? (
                                  <>
                                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>{trade.size}</td>
                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d' }}>${parseFloat(trade.openPrice ?? 0).toFixed(4)}</td>
                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d' }}>${parseFloat(trade.closePrice ?? 0).toFixed(4)}</td>
                                    <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: pnl >= 0 ? '#198754' : '#dc3545' }}>
                                      {pnl >= 0 ? '+' : ''}${pnl.toFixed(4)}
                                    </td>
                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: '#dc3545' }}>${fees.toFixed(4)}</td>
                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontSize: 11, whiteSpace: 'nowrap' }}>
                                      {formatTradeClosedDisplay(trade)}
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>${parseFloat(trade.amount ?? trade.size ?? 0).toFixed(2)}</td>
                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d' }}>${parseFloat(trade.price ?? trade.entryPrice ?? 0).toFixed(4)}</td>
                                    <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: pnl >= 0 ? '#198754' : '#dc3545' }}>
                                      {pnl >= 0 ? '+' : ''}${pnl.toFixed(4)}
                                    </td>
                                    <td style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontSize: 11, whiteSpace: 'nowrap' }}>
                                      {formatTradeClosedDisplay(trade)}
                                    </td>
                                  </>
                                )}
                              </tr>
                            );
                          }) : (
                            <tr><td colSpan={fullAnalyticsDataSource === 'kucoin' ? 8 : 6} style={{ padding: '16px 8px', textAlign: 'center', color: '#adb5bd' }}>No trades found</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Open Positions — shown in both KuCoin Live and DB modes */}
                  {(
                    <div style={{ marginBottom: 20 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#212529' }}>Open Positions</div>
                        <span style={{ fontSize: 12, padding: '3px 8px', background: '#e9ecef', color: '#495057', borderRadius: 4, fontWeight: 500 }}>Live DB</span>
                      </div>
                      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>
                        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', minWidth: 260 }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Position</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Amount @ Entry</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Unrealised P&L</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fullAnalyticsPositions.length > 0 ? fullAnalyticsPositions.map((pos: any, i: number) => {
                              const pnl = parseFloat(pos.unrealizedPnl ?? 0);
                              return (
                                <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                  <td style={{ padding: '6px 8px', fontWeight: 500 }}>
                                    {pos.symbol}
                                    <span style={{
                                      marginLeft: 6, fontSize: 11, padding: '2px 6px', borderRadius: 3, fontWeight: 600,
                                      background: pos.side === 'LONG' ? '#d1e7dd' : '#f8d7da',
                                      color: pos.side === 'LONG' ? '#0f5132' : '#842029'
                                    }}>{pos.side}</span>
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d' }}>
                                    ${parseFloat(pos.amount ?? 0).toFixed(2)} @ ${parseFloat(pos.entryPrice ?? 0).toFixed(4)}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: pnl >= 0 ? '#198754' : '#dc3545' }}>
                                    {pnl >= 0 ? '+' : ''}${pnl.toFixed(4)}
                                  </td>
                                </tr>
                              );
                            }) : (
                              <tr><td colSpan={3} style={{ padding: '16px 8px', textAlign: 'center', color: '#adb5bd', fontSize: 12 }}>
                                No open positions right now — bot is waiting for next entry signal
                              </td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* P&L History */}
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#212529', marginBottom: 10 }}>
                      {fullAnalyticsDataSource === 'kucoin' ? 'P&L History (KuCoin — all time)' : '30-Day P&L History (DB)'}
                    </div>
                    {fullAnalyticsPnlHistory.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                              <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Date</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Daily P&L</th>
                              <th style={{ textAlign: 'right', padding: '6px 8px', color: '#6c757d', fontWeight: 500 }}>Cumulative P&L</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...fullAnalyticsPnlHistory].reverse().map((entry: any, i: number) => {
                              const daily = parseFloat(entry.pnl ?? entry.dailyPnl ?? 0);
                              const cum = parseFloat(entry.cumulativePnl ?? 0);
                              return (
                                <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                                  <td style={{ padding: '6px 8px', color: '#6c757d' }}>{entry.date}</td>
                                  <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: daily >= 0 ? '#198754' : '#dc3545' }}>
                                    {daily >= 0 ? '+' : ''}${daily.toFixed(4)}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: cum >= 0 ? '#198754' : '#dc3545' }}>
                                    {cum >= 0 ? '+' : ''}${cum.toFixed(4)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ padding: '16px 0', textAlign: 'center', color: '#adb5bd', fontSize: 12 }}>
                        No P&L history available
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailTradingInterface;
